import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import { Send, Eye, Users, Smartphone, Mail, CheckCircle, AlertTriangle, Plus, X, Palette, Zap, TrendingUp, Award, MessageSquare, Lightbulb, Gift, Newspaper, Loader, Save, FileText, Trash2, Edit2, Copy, Info, Search, ArrowRight, BarChart3, User, Image, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import EmailAnalytics from './EmailAnalytics';
import MediaLibraryTab from './MediaLibraryTab';
import RichTextEditor, { normalizeSpaces } from '../ui/RichTextEditor';
import './CuratorCommunicationsHub.css';

/**
 * CuratorCommunicationsHub
 * 
 * A communications tool for curators to send notifications across ALL their collections
 * Based on the proven CommunicationPanel from CollectionDetailAdmin
 * but extended to work system-wide rather than per-collection
 * 
 * Features three tabs:
 * - Tab 1: Send Communications (use system or custom types)
 * - Tab 2: Email Templates (create/edit organization email templates)
 * - Tab 3: Email Analytics
 */

const CuratorCommunicationsHub = ({ collectionId: propCollectionId = null, collectionData: propCollectionData = null }) => {
  // Get organization context from router outlet
  const outletContext = useOutletContext() || {};
  const { selectedOrganization: contextOrganization } = outletContext;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Single artist targeting from URL params (from artist detail page)
  const [singleArtistMode, setSingleArtistMode] = useState(false);
  const [singleArtist, setSingleArtist] = useState(null);
  
  const [activeTab, setActiveTab] = useState('send'); // 'send', 'templates', 'create', 'analytics', or 'media'
  const [selectedCommunication, setSelectedCommunication] = useState('');
  const [targetAudience, setTargetAudience] = useState('all_artists');
  const [customMessage, setCustomMessage] = useState('');
  const [testMode, setTestMode] = useState(true);
  const [skipAlreadySent, setSkipAlreadySent] = useState(false);
  const [alreadySentCount, setAlreadySentCount] = useState(null); // Count of recipients already sent for current subject
  const [checkingSentCount, setCheckingSentCount] = useState(false); // Loading state for already-sent lookup
  const [isLoading, setIsLoading] = useState(false);
  const [sendingStatus, setSendingStatus] = useState(''); // Detailed sending status message
  const [results, setResults] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventDetails, setEventDetails] = useState({
    event_date: '',
    event_location: '',
    setup_time: ''
  });

  // Organization context - prefer context from router, fall back to fetching
  const [organization, setOrganization] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);

  // Collection context - can come from prop or be selected
  // Remember last selected collection in localStorage
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(() => {
    if (propCollectionId) return propCollectionId;
    const saved = localStorage.getItem('communications_last_collection_id');
    return saved ? parseInt(saved, 10) : null;
  });
  const [selectedCollection, setSelectedCollection] = useState(propCollectionData);
  const [collectionSearch, setCollectionSearch] = useState('');
  const isCollectionContext = !!propCollectionId; // True if embedded in collection detail page

  // Persist collection selection to localStorage
  useEffect(() => {
    if (selectedCollectionId && !isCollectionContext) {
      localStorage.setItem('communications_last_collection_id', selectedCollectionId.toString());
    }
  }, [selectedCollectionId, isCollectionContext]);

  // Parse URL params for single artist targeting (from artist detail page)
  useEffect(() => {
    const targetArtistUuid = searchParams.get('targetArtist');
    const artistEmail = searchParams.get('artistEmail');
    const artistName = searchParams.get('artistName');
    const collectionParam = searchParams.get('collection');
    
    if (targetArtistUuid && artistEmail) {
      // Single artist mode - coming from artist detail page
      setSingleArtistMode(true);
      setSingleArtist({
        uuid: targetArtistUuid,
        email: artistEmail,
        first_name: artistName || '',
        nickname: artistName || ''
      });
      setTargetAudience('single_artist');
      
      // Set collection if provided
      if (collectionParam) {
        setSelectedCollectionId(parseInt(collectionParam, 10));
      }
    }
  }, [searchParams]);

  // Organization members for test mode recipient selection
  const [orgMembers, setOrgMembers] = useState([]);
  const [selectedTestAdminIds, setSelectedTestAdminIds] = useState([]);
  const [showTestAdminsModal, setShowTestAdminsModal] = useState(false);
  
  // Email preview for Send tab
  const [showSendEmailPreview, setShowSendEmailPreview] = useState(false);
  const [previewArtistUuid, setPreviewArtistUuid] = useState(null);
  const [previewPortfolioData, setPreviewPortfolioData] = useState(null);
  const [loadingPreviewData, setLoadingPreviewData] = useState(false);
  const [previewArtistSearch, setPreviewArtistSearch] = useState('');
  const [showPreviewArtistResults, setShowPreviewArtistResults] = useState(false);
  
  // Recipients list modal
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsPerPage, setRecipientsPerPage] = useState(20);
  const [recipientsSortField, setRecipientsSortField] = useState('name');
  const [recipientsSortDir, setRecipientsSortDir] = useState('asc');
  
  // Excluded users from email send
  const [excludedUserIds, setExcludedUserIds] = useState(new Set());

  // Email templates from database
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState('');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Inline compose state for Send tab
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [showComposeEditor, setShowComposeEditor] = useState(true);

  // Custom communication types (local state for demo)
  const [customCommunicationTypes, setCustomCommunicationTypes] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newType, setNewType] = useState({
    title: '',
    description: '',
    audience: 'all_artists',
    emailSubject: '',
    pushTitle: '',
    pushMessage: '',
    color: '#7c3aed'
  });

  // New template form state
  const [newTemplate, setNewTemplate] = useState({
    template_key: '',
    subject: '',
    html_body: '',
    plain_text_body: '',
    audience_type: 'accepted_artists',
    mandrill_template_name: ''
  });

  // Available merge variables for templates
  const mergeVariables = [
    { key: '{artist_name}', description: 'Artist\'s display name' },
    { key: '{first_name}', description: 'Artist\'s first name' },
    { key: '{collection_name}', description: 'Collection/show title' },
    { key: '{organization_name}', description: 'Organization name' },
    { key: '{accepted_artworks}', description: 'List of accepted artwork titles' },
    { key: '{important_dates}', description: 'Important dates HTML block' },
    { key: '{acceptance_url}', description: 'Link to accept terms' },
    { key: '{event_readiness_url}', description: 'Link to artist event readiness checklist' },
    { key: '{submission_apply_url}', description: 'Link to apply (with email pre-filled)' },
    { key: '{submission_deadline}', description: 'Submission deadline date' },
    { key: '{submission_id}', description: 'Submission ID' },
    { key: '{drop_off_date}', description: 'Artwork drop-off date' },
    { key: '{drop_off_location}', description: 'Drop-off location address' },
    { key: '{pickup_date}', description: 'Artwork pickup date' },
    { key: '{pickup_location}', description: 'Pickup location address' },
    { key: '{contact_email}', description: 'Organization contact email' },
    { key: '{organization_logo}', description: 'Organization logo URL' },
    // Portfolio status merge variables (for vc_artists audience)
    { key: '{portfolio_summary}', description: 'Artist portfolio summary with stats' },
    { key: '{artwork_list}', description: 'Table of artworks with prints & location' },
    { key: '{total_artworks}', description: 'Total public artwork count' },
    { key: '{for_sale_count}', description: 'Number of artworks for sale' },
    { key: '{sold_count}', description: 'Number of sold artworks' },
    { key: '{prints_available_count}', description: 'Number of artworks with prints' },
    { key: '{located_count}', description: 'Number of artworks at galleries' },
    { key: '{artist_studio_url}', description: 'Link to artist studio' },
    { key: '{apply_url}', description: 'Link to membership application' },
    // Artist promo code merge variables
    { key: '{promo_codes}', description: 'Artist\'s promo codes with discount info' },
    { key: '{promo_links}', description: 'Artist\'s promo codes as clickable ticket links' },
    // Payout merge variables
    { key: '{payout_amount}', description: 'Artist\'s payout amount (e.g. $292.50)' },
    { key: '{payout_status}', description: 'Current payout status (pending / emailed)' },
    { key: '{sales_total}', description: 'Total artwork sales before commission' },
    { key: '{commission_pct}', description: 'Commission percentage (e.g. 30)' },
    { key: '{payout_line_items}', description: 'HTML table of sold artworks with prices' }
  ];

  // All artists and artworks across curator's collections
  const [allArtists, setAllArtists] = useState([]);
  const [allArtworks, setAllArtworks] = useState([]);
  
  // Submission-based audience data for selected collection
  const [submissionAudienceData, setSubmissionAudienceData] = useState({
    all_artists: [],           // All artists in artist_submission_mapping
    paid_artists: [],          // Artists who paid
    unpaid_artists: [],        // Artists who haven't paid
    artists_with_artwork: [],  // Artists who submitted artwork
    no_artwork_submitted: [],  // Paid but no artwork submitted
    accepted_artists: [],      // Artists with accepted artwork
    not_accepted: [],          // Artists who submitted artwork but none were accepted
    organization_contacts: [],  // External organization members to invite
    checked_in_artists: [],     // Artists with at least one artwork checked in
    not_checked_in_artists: [], // Accepted artists whose artwork is NOT checked in yet
    artists_pending_payout: []  // Artists with a payout record in pending or emailed status
  });
  const [acceptedArtworksCount, setAcceptedArtworksCount] = useState(0); // Actual count of accepted artworks
  const [audienceLoading, setAudienceLoading] = useState(false);
  
  // Platform-wide audiences (not collection-specific)
  const [platformAudiences, setPlatformAudiences] = useState({
    all_users: [],           // All platform users
    all_artists_ever: [],    // All users who have ever submitted to any collection
    all_collectors: [],      // Users with collector preferences
    prospects_all: [],       // All prospects
    prospects_linked: [],    // Prospects linked to user accounts
    prospects_unlinked: [],  // Prospects not yet linked
    vc_artists: [],          // VibrantCast artists with public artworks (for portfolio status emails)
    event_186_non_buyers: [] // Users + prospects (deduped by email) who have not bought a ticket for event 186
  });
  const [platformAudienceLoading, setPlatformAudienceLoading] = useState(false);
  const [excludeCollectionArtists, setExcludeCollectionArtists] = useState(false); // Toggle to exclude artists in selected collection
  
  // Submission details for the selected collection (for merge vars)
  const [submissionDetails, setSubmissionDetails] = useState({
    submission_id: null,
    deadline: null
  });

  // Default CAVA-style email templates for organizations
  const defaultEmailTemplates = [
    {
      template_key: 'submission_reminder',
      subject: 'Reminder: Submit your artwork for {collection_name}',
      description: 'Remind paid artists to submit their artwork before deadline',
      audience_type: 'no_artwork_submitted',
      html_body: `<p>Dear {artist_name},</p>
<p>This is a friendly reminder that you have registered for <strong>{collection_name}</strong> but haven't submitted your artwork yet.</p>
<p>Don't miss your chance to be part of this exciting exhibition!</p>
<h3>Submission Deadline</h3>
<p><strong>{submission_deadline}</strong></p>
<p style="text-align: center; margin: 24px 0;">
<a href="https://www.vibrantcast.com/artist/submissions/{submission_id}?submissionId={submission_id}" style="background-color: #7c3aed; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: 600;">Submit Your Artwork →</a>
</p>
<h3>How to Submit</h3>
<ol>
<li>Log in to VibrantCast</li>
<li>Click on <strong>Artist Call Outs</strong></li>
<li>Select <strong>{collection_name}</strong></li>
<li>Upload your artwork images and details</li>
</ol>
<p>If you have any questions about the submission process, please don't hesitate to reach out to us at {contact_email}.</p>
<p>We look forward to seeing your work!</p>
<p>Best regards,<br/>{organization_name}</p>`
    },
    {
      template_key: 'deadline_warning',
      subject: 'URGENT: Submission deadline approaching for {collection_name}',
      description: 'Final warning before submission deadline closes',
      audience_type: 'no_artwork_submitted',
      html_body: `<p>Dear {artist_name},</p>
<p><strong>⚠️ URGENT: The submission deadline for {collection_name} is in less than 24 hours!</strong></p>
<p>You registered for this exhibition but we haven't received your artwork submission yet.</p>
<h3>Deadline: {submission_deadline}</h3>
<p>After this time, submissions will be closed and you will not be able to participate in this exhibition.</p>
<p style="text-align: center; margin: 24px 0;">
<a href="https://www.vibrantcast.com/artist/submissions/{submission_id}?submissionId={submission_id}" style="background-color: #dc2626; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">Submit Now →</a>
</p>
<p>Or log in to VibrantCast, click on <strong>Artist Call Outs</strong>, and select <strong>{collection_name}</strong>.</p>
<p>If you're having technical difficulties or need assistance, contact us immediately at {contact_email}.</p>
<p>Best regards,<br/>{organization_name}</p>`
    },
    {
      template_key: 'event_announcement',
      subject: 'You\'re Invited: {event_name} at {collection_name}',
      description: 'Announce exhibition opening or special event',
      audience_type: 'all_artists',
      html_body: `<p>Dear {artist_name},</p>
<p>You're invited to join us for <strong>{event_name}</strong>!</p>
<h3>Event Details</h3>
<ul>
<li><strong>Date:</strong> {event_date}</li>
<li><strong>Time:</strong> {event_time}</li>
<li><strong>Location:</strong> {event_location}</li>
</ul>
<p>{event_description}</p>
<p>We hope to see you there!</p>
<p>Best regards,<br/>{organization_name}</p>`
    },
    {
      template_key: 'general_update',
      subject: 'Update from {collection_name}',
      description: 'General communication to artists',
      audience_type: 'all_artists',
      html_body: `<p>Dear {artist_name},</p>
<p>{custom_message}</p>
<p>If you have any questions, please contact us at {contact_email}.</p>
<p>Best regards,<br/>{organization_name}</p>`
    },
    {
      template_key: 'artwork_accepted',
      subject: 'Congratulations! Your artwork was accepted for {collection_name}',
      description: 'Notify artists their work was accepted with important dates',
      audience_type: 'accepted_artists',
      html_body: `<p>Dear {artist_name},</p>
<p>Congratulations! We are pleased to inform you that <strong>your artwork has been accepted</strong> for {collection_name}.</p>
<p>Your accepted piece(s):</p>
{accepted_artworks}
<p>Please note that some of your other entries were not accepted for this exhibition.</p>
<h3>Important Dates</h3>
{important_dates}
<h3>Sales & Commissions</h3>
<p>All sales will be processed through VibrantCast with a 30% commission to the gallery. You will receive 70% of the sale price.</p>
<p>Please click the button below to review and accept the exhibition terms:</p>
<p><a href="{acceptance_url}" style="background-color: #7c3aed; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">Accept Exhibition Terms</a></p>
<p>We look forward to featuring your work!</p>
<p>Best regards,<br/>{organization_name}</p>`
    },
    {
      template_key: 'non_acceptance',
      subject: 'Thank you for your submission to {collection_name}',
      description: 'Graceful notification when artwork is not selected',
      audience_type: 'not_accepted',
      html_body: `<p>Dear {artist_name},</p>
<p>Thank you for submitting your artwork to {collection_name}. We truly appreciate your interest and the time you took to share your work with us.</p>
<p>After careful consideration, we regret to inform you that your submission was not selected for this exhibition. Please know that this decision does not diminish the value of your artistic expression.</p>
<p>We encourage you to continue creating and to apply for future exhibitions. Your unique perspective is valuable, and we hope to see your work again.</p>
<p>Thank you for being part of our artistic community.</p>
<p>Warm regards,<br/>{organization_name}</p>`
    },
    {
      template_key: 'drop_off_reminder',
      subject: 'Reminder: Artwork drop-off for {collection_name}',
      description: 'Friendly reminder with drop-off date/time/location',
      audience_type: 'accepted_artists',
      html_body: `<p>Dear {artist_name},</p>
<p>This is a friendly reminder that artwork drop-off for <strong>{collection_name}</strong> is coming up soon!</p>
<h3>Drop-Off Details</h3>
<ul>
<li><strong>Date:</strong> {drop_off_date}</li>
<li><strong>Location:</strong> {drop_off_location}</li>
</ul>
<p>Your accepted piece(s) for this exhibition:</p>
{accepted_artworks}
<h3>What to Bring</h3>
<ul>
<li>Your artwork(s), properly wrapped for transport</li>
<li>Any special hanging/display instructions</li>
<li>Photo ID</li>
</ul>
<p>If you have any questions or need to make special arrangements, please contact us at {contact_email}.</p>
<p>We look forward to seeing you!</p>
<p>Best regards,<br/>{organization_name}</p>`
    },
    {
      template_key: 'pick_up_reminder',
      subject: 'Reminder: Artwork pickup for {collection_name}',
      description: 'Reminder with pickup date/time and contact info',
      audience_type: 'accepted_artists',
      html_body: `<p>Dear {artist_name},</p>
<p>This is a reminder that artwork pickup for <strong>{collection_name}</strong> is scheduled soon.</p>
<h3>Pickup Details</h3>
<ul>
<li><strong>Date:</strong> {pickup_date}</li>
<li><strong>Location:</strong> {pickup_location}</li>
</ul>
<p>Please arrive during the designated pickup hours with photo ID.</p>
<h3>Can't Pick Up in Person?</h3>
<p>If you cannot personally pick up your artwork, please contact us at {contact_email} to make alternative arrangements. You may authorize someone to pick up on your behalf with written permission.</p>
<p>Thank you for being part of this exhibition!</p>
<p>Best regards,<br/>{organization_name}</p>`
    },
    {
      template_key: 'payout_notification',
      subject: 'Your artwork payout from {collection_name}',
      description: 'Notify artists that their payout has been processed',
      audience_type: 'artists_pending_payout',
      html_body: `<p>Dear {artist_name},</p>
<p>Thank you for participating in <strong>{collection_name}</strong>!</p>
<p>We have processed your artwork sales and your payout of <strong>{payout_amount}</strong> is now being prepared.</p>
<p>You can expect to receive your payment via the method on file. If you have any questions about your payout or need to update your payment details, please contact us at {contact_email}.</p>
<p>Thank you again for sharing your work with us — we look forward to seeing you at future exhibitions!</p>
<p>Warm regards,<br/>{organization_name}</p>`
    }
  ];

  // Communication templates (same as CollectionDetailAdmin)
  const communicationOptions = [
    {
      value: 'submission_reminder',
      label: 'Submission Reminder',
      description: 'Remind paid artists to submit their artwork',
      audience: 'no_artwork_submitted',
      email_template: 'submission_reminder',
      push: {
        title: 'Submit Your Artwork',
        message: 'Don\'t forget to submit for {collection_name} - deadline approaching!',
        category: 'collection_deadline'
      }
    },
    {
      value: 'deadline_warning',
      label: 'Urgent Deadline Warning',
      description: 'Final warning before submission deadline',
      audience: 'no_artwork_submitted',
      email_template: 'deadline_urgent',
      push: {
        title: 'URGENT: Deadline Soon!',
        message: 'Submit for {collection_name} - deadline in 24 hours!',
        category: 'collection_urgent'
      }
    },
    {
      value: 'artwork_accepted',
      label: 'Acceptance Notification',
      description: 'Congratulate artists on accepted artwork',
      audience: 'accepted_artists',
      email_template: 'artwork_acceptance',
      push: {
        title: 'Congratulations!',
        message: 'Your artwork was accepted for {collection_name}!',
        category: 'collection_acceptance'
      }
    },
    {
      value: 'event_announcement',
      label: 'Event Details',
      description: 'Share event logistics and details',
      audience: 'accepted_artists',
      email_template: 'event_details',
      push: {
        title: 'Event Details Available',
        message: '{collection_name} opening soon - check your email!',
        category: 'event_notification'
      }
    },
    {
      value: 'general_update',
      label: 'General Update',
      description: 'Custom message to selected audience',
      audience: 'all_artists',
      email_template: 'general_update',
      push: {
        title: 'Update from VibrantCast',
        message: 'New update about your collections',
        category: 'collection_update'
      }
    },
    {
      value: 'payout_notification',
      label: '💰 Payout Notification',
      description: 'Notify artists that their payout is being processed',
      audience: 'artists_pending_payout',
      email_template: 'payout_notification',
      push: {
        title: 'Your Payout is Ready',
        message: 'Your artwork payout from {collection_name} is being processed!',
        category: 'payout_notification'
      }
    }
  ];

  // Platform-wide communication options (no collection required)
  const platformCommunicationOptions = [
    {
      value: 'platform_announcement',
      label: '🌐 All Platform Users',
      description: 'Send to all registered users',
      audience: 'all_users',
      email_template: 'general_update',
      isPlatformWide: true,
      push: {
        title: 'VibrantCast Announcement',
        message: 'Important update from VibrantCast',
        category: 'platform_announcement'
      }
    },
    {
      value: 'all_artists_outreach',
      label: '🎨 All Artists Ever',
      description: 'Users who submitted to any collection',
      audience: 'all_artists_ever',
      email_template: 'general_update',
      isPlatformWide: true,
      push: {
        title: 'Artist Update',
        message: 'Update for VibrantCast artists',
        category: 'artist_outreach'
      }
    },
    {
      value: 'all_collectors_outreach',
      label: '🖼️ All Collectors',
      description: 'Users with collector preferences',
      audience: 'all_collectors',
      email_template: 'general_update',
      isPlatformWide: true,
      push: {
        title: 'Collector Update',
        message: 'Update for VibrantCast collectors',
        category: 'collector_outreach'
      }
    },
    {
      value: 'prospect_outreach',
      label: '🎯 All Prospects',
      description: 'Reach out to all prospects',
      audience: 'prospects_all',
      email_template: 'general_update',
      isPlatformWide: true,
      push: {
        title: 'VibrantCast Opportunity',
        message: 'New opportunity from VibrantCast',
        category: 'prospect_outreach'
      }
    },
    {
      value: 'prospect_linked_outreach',
      label: '🔗 Linked Prospects',
      description: 'Prospects linked to user accounts',
      audience: 'prospects_linked',
      email_template: 'general_update',
      isPlatformWide: true,
      push: {
        title: 'VibrantCast Update',
        message: 'Update for linked prospects',
        category: 'prospect_linked'
      }
    },
    {
      value: 'prospect_unlinked_outreach',
      label: '📧 Unlinked Prospects',
      description: 'Prospects not yet linked to accounts',
      audience: 'prospects_unlinked',
      email_template: 'general_update',
      isPlatformWide: true,
      push: {
        title: 'VibrantCast Invitation',
        message: 'Invitation to join VibrantCast',
        category: 'prospect_unlinked'
      }
    }
  ];

  // VibrantCast-specific communication options (org_id = 2 only)
  const vibrantcastCommunicationOptions = [
    {
      value: 'event_186_non_buyers_outreach',
      label: '🎟️ Event 186 Non-Buyers',
      description: 'All platform users + all prospects (deduped), excluding ticket buyers for event 186',
      audience: 'event_186_non_buyers',
      email_template: 'general_update',
      isPlatformWide: true,
      isVibrantCastOnly: true,
      push: {
        title: 'Festival Invitation',
        message: 'You are invited to Festival of Independent Artists',
        category: 'event_outreach'
      }
    },
    {
      value: 'portfolio_status',
      label: '📊 Portfolio Status',
      description: 'Send artists their portfolio status with artworks, prints & locations',
      audience: 'vc_artists',
      email_template: 'portfolio_status',
      isPlatformWide: true,
      isVibrantCastOnly: true,
      push: {
        title: 'Your VibrantCast Portfolio Status',
        message: 'Check your portfolio status and prepare for the Artist Directory launch!',
        category: 'artist_outreach'
      }
    }
  ];

  // Organization contacts communication option (for non-VibrantCast orgs)
  const orgContactsCommunicationOption = {
    value: 'org_contacts_outreach',
    label: '📇 All Contacts',
    description: 'Send to all organization contacts',
    audience: 'organization_contacts',
    email_template: 'general_update',
    isOrgWide: true,
    push: {
      title: 'Organization Update',
      message: 'Update from your organization',
      category: 'org_contacts'
    }
  };

  // Suggested communication types for creation tab
  const suggestedCommunicationTypes = [
    {
      id: 'newsletter',
      title: 'Newsletter/Blog Update',
      description: 'Share news, updates, and behind-the-scenes content',
      defaultAudience: 'all_artists',
      defaultEmailSubject: 'Latest news from {collection_name}',
      defaultPushTitle: 'New Update',
      color: '#3b82f6'
    },
    {
      id: 'weekly_digest',
      title: 'Weekly Digest',
      description: 'Regular summary of important information',
      defaultAudience: 'all_artists',
      defaultEmailSubject: 'Your weekly digest from {collection_name}',
      defaultPushTitle: 'Weekly Digest',
      color: '#8b5cf6'
    },
    {
      id: 'artist_spotlight',
      title: 'Artist Spotlight',
      description: 'Feature and celebrate artist work',
      defaultAudience: 'all_artists',
      defaultEmailSubject: 'Artist Spotlight: {artist_name}',
      defaultPushTitle: 'Artist Spotlight',
      color: '#ec4899'
    },
    {
      id: 'feedback_survey',
      title: 'Call for Feedback/Survey',
      description: 'Request feedback and gather insights',
      defaultAudience: 'accepted_artists',
      defaultEmailSubject: 'We\'d love your feedback!',
      defaultPushTitle: 'Quick Question',
      color: '#f59e0b'
    },
    {
      id: 'behind_scenes',
      title: 'Behind-the-Scenes Content',
      description: 'Share exclusive behind-the-scenes updates',
      defaultAudience: 'accepted_artists',
      defaultEmailSubject: 'Exclusive: Behind the scenes at {collection_name}',
      defaultPushTitle: 'Behind the Scenes',
      color: '#10b981'
    },
    {
      id: 'exclusive_opp',
      title: 'Exclusive Opportunity',
      description: 'Offer special opportunities to artists',
      defaultAudience: 'accepted_artists',
      defaultEmailSubject: 'Special Opportunity for You!',
      defaultPushTitle: 'Exclusive Opportunity',
      color: '#06b6d4'
    },
    {
      id: 'gallery_news',
      title: 'Gallery News & Updates',
      description: 'General gallery announcements and news',
      defaultAudience: 'all_artists',
      defaultEmailSubject: 'Gallery News from {collection_name}',
      defaultPushTitle: 'Gallery News',
      color: '#6366f1'
    }
  ];

  // Audience labels
  const audienceLabels = {
    all_artists: 'All Artists',
    accepted_artists: 'Accepted Artists',
    unpaid_artists: 'Unpaid Artists',
    no_artwork_submitted: 'No Artwork Submitted',
    single_artist: singleArtist ? `${singleArtist.nickname || singleArtist.first_name || singleArtist.email}` : 'Selected Artist',
    organization_contacts: '📇 All Contacts',
    needs_terms: 'Needs Terms',
    checked_in_artists: '✅ Checked-In Artists',
    not_checked_in_artists: '📦 Not Checked-In Yet',
    artists_pending_payout: '💰 Pending Payout',
    // Platform-wide audiences (no collection required) - VibrantCast only
    all_users: '🌐 All Platform Users',
    all_artists_ever: '🎨 All Artists Ever',
    all_collectors: '🖼️ All Collectors',
    prospects_all: '🎯 All Prospects',
    prospects_linked: '🔗 Linked Prospects',
    prospects_unlinked: '📧 Unlinked Prospects',
    // VibrantCast-specific audiences
    vc_artists: '📊 VibrantCast Artists (with artworks)',
    event_186_non_buyers: '🎟️ Event 186 Non-Buyers'
  };

  // Check if current organization is VibrantCast (org_id = 2) for platform-wide features
  const isVibrantCastOrg = organizationId === 2;

  // Use context organization when available
  useEffect(() => {
    if (contextOrganization) {
      console.log('Using organization from context:', contextOrganization.name);
      setOrganization(contextOrganization);
      setOrganizationId(contextOrganization.organization_id);
    }
  }, [contextOrganization]);

  // Load curator's artists and artworks on mount
  useEffect(() => {
    console.log('CuratorCommunicationsHub mounted');
    loadCuratorData();
  }, []);

  // Reload collections when organization context changes
  useEffect(() => {
    if (contextOrganization?.organization_id) {
      console.log('Organization context changed, reloading data for:', contextOrganization.name);
      loadCuratorData();
    }
  }, [contextOrganization?.organization_id]);

  // Load email templates when organization changes
  useEffect(() => {
    if (organizationId) {
      loadEmailTemplates();
      loadOrgMembers();
    }
  }, [organizationId]);

  // Load audience data when collection changes
  useEffect(() => {
    const collectionToLoad = selectedCollectionId || propCollectionId;
    console.log('Collection useEffect triggered, collectionToLoad:', collectionToLoad);
    if (collectionToLoad) {
      loadAudienceForCollection(collectionToLoad);
    }
  }, [selectedCollectionId, propCollectionId]);
  
  // Reload org members and templates when collection's org changes
  useEffect(() => {
    const collectionOrgId = selectedCollection?.organization_id || propCollectionData?.organization_id;
    if (collectionOrgId) {
      console.log('Collection org changed to:', collectionOrgId);
      loadEmailTemplatesForOrg(collectionOrgId);
      loadOrgMembersForOrg(collectionOrgId);
    }
  }, [selectedCollection?.organization_id, propCollectionData?.organization_id]);

  // Load audience data from artist_submission_mapping for a specific collection
  const loadAudienceForCollection = async (collectionId) => {
    try {
      setAudienceLoading(true);
      console.log('Loading audience for collection:', collectionId);

      // 1. Get submission_id and submission_deadline directly from submissions_details
      const { data: submissionData, error: submissionError } = await supabase
        .from('submissions_details')
        .select('submission_id, title, submission_deadline')
        .eq('collection_id', collectionId)
        .single();

      if (submissionError || !submissionData) {
        console.log('No submission found for collection', collectionId, submissionError);
        setSubmissionDetails({ submission_id: null, deadline: null });
        // Fall back to artwork-based calculation (preserve organization_contacts)
        setSubmissionAudienceData(prev => ({
          ...prev,
          all_artists: allArtists,
          paid_artists: allArtists,
          unpaid_artists: [],
          artists_with_artwork: allArtists,
          no_artwork_submitted: [],
          accepted_artists: allArtists.filter(a => 
            allArtworks.some(aw => aw.artist?.uuid === a.uuid && aw.accepted)
          )
        }));
        setAudienceLoading(false);
        return;
      }

      const submissionId = submissionData.submission_id;
      const deadline = submissionData.submission_deadline;
      
      // Store submission details for merge vars
      setSubmissionDetails({
        submission_id: submissionId,
        deadline: deadline ? new Date(deadline).toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        }) : null
      });
      
      console.log('Found submission_id:', submissionId, 'deadline:', deadline);

      // 2. Get all artists in artist_submission_mapping with their user details
      const { data: mappingData, error: mappingError } = await supabase
        .from('artist_submission_mapping')
        .select('id, user_id, paid, accepted, accepted_at, terms_accepted')
        .eq('submission_id', submissionId);

      if (mappingError) throw mappingError;

      const userIds = mappingData?.map(m => m.user_id).filter(Boolean) || [];
      
      if (userIds.length === 0) {
        console.log('No artists in submission mapping');
        setSubmissionAudienceData(prev => ({
          ...prev,
          all_artists: [],
          paid_artists: [],
          unpaid_artists: [],
          artists_with_artwork: [],
          no_artwork_submitted: [],
          accepted_artists: []
        }));
        setAudienceLoading(false);
        return;
      }

      // 3. Get user details
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('uuid, first_name, last_name, email, nickname')
        .in('uuid', userIds);

      if (usersError) throw usersError;

      // 4. Get artworks in this collection to see who actually submitted
      const { data: artworkMappings, error: artworkError } = await supabase
        .from('collection_vc_mapping')
        .select('vc_id, accepted')
        .eq('collection_id', collectionId);

      if (artworkError) throw artworkError;

      const vcIds = artworkMappings?.map(m => m.vc_id) || [];
      
      // 5. Get which artists have artworks in collection
      let artistsWithArtworkUuids = new Set();
      let artistsWithAcceptedArtworkUuids = new Set();
      let checkedInArtistUuids = new Set();
      
      if (vcIds.length > 0) {
        const { data: vcsData, error: vcsError } = await supabase
          .from('vcs')
          .select('vc_id, uuid, location')
          .in('vc_id', vcIds);

        if (!vcsError && vcsData) {
          vcsData.forEach(vc => {
            if (vc.uuid) artistsWithArtworkUuids.add(vc.uuid);
          });
          
          // Check which have accepted artwork
          let acceptedArtworkCount = 0;
          artworkMappings.forEach(mapping => {
            const vc = vcsData.find(v => v.vc_id === mapping.vc_id);
            if (vc && mapping.accepted) {
              artistsWithAcceptedArtworkUuids.add(vc.uuid);
              acceptedArtworkCount++;
              // Track checked-in artists (location is set = checked in)
              if (vc.location) {
                checkedInArtistUuids.add(vc.uuid);
              }
            }
          });
          setAcceptedArtworksCount(acceptedArtworkCount);
        }
      }

      // 6. Build audience lists from submission mapping
      console.log('First 3 mapping records:', mappingData?.slice(0, 3));
      
      // Debug: Find all accepted records
      const acceptedMappings = mappingData?.filter(m => m.accepted === true) || [];
      console.log(`Found ${acceptedMappings.length} records with accepted=true:`, acceptedMappings);
      console.log('terms_accepted values:', acceptedMappings.map(m => ({
        user_id: m.user_id,
        accepted: m.accepted,
        terms_accepted: m.terms_accepted,
        terms_keys: m.terms_accepted ? Object.keys(m.terms_accepted).length : 0
      })));
      
      const allArtistsInSubmission = (usersData || []).map(user => {
        const mapping = mappingData.find(m => m.user_id === user.uuid);
        return {
          ...user,
          has_paid: mapping?.paid || false,
          has_submitted_artwork: artistsWithArtworkUuids.has(user.uuid),
          has_accepted_artwork: artistsWithAcceptedArtworkUuids.has(user.uuid),
          accepted_terms: mapping?.accepted === true && mapping?.accepted_at !== null,
          submission_mapping: mapping // Store mapping for later use
        };
      });

      // 7. Find artists with accepted artwork who are NOT in artist_submission_mapping
      // These are artists whose artwork was manually added to the collection
      const missingAcceptedArtistUuids = [...artistsWithAcceptedArtworkUuids].filter(
        uuid => !userIds.includes(uuid)
      );
      
      let additionalAcceptedArtists = [];
      if (missingAcceptedArtistUuids.length > 0) {
        console.log(`Found ${missingAcceptedArtistUuids.length} artists with accepted artwork not in submission mapping`);
        const { data: missingUsers } = await supabase
          .from('users')
          .select('uuid, first_name, last_name, email, nickname')
          .in('uuid', missingAcceptedArtistUuids);
        
        if (missingUsers) {
          additionalAcceptedArtists = missingUsers.map(user => ({
            ...user,
            has_paid: true, // Treat as paid since curator manually added
            has_submitted_artwork: true,
            has_accepted_artwork: true,
            manually_added: true // Flag for reference
          }));
        }
      }

      // Combine both lists
      const combinedArtists = [...allArtistsInSubmission, ...additionalAcceptedArtists];

      // Debug: Check needs_terms logic
      // "Needs Terms" = Artists with ACCEPTED ARTWORK who haven't accepted terms yet
      // Check: has_accepted_artwork = true AND terms_accepted is empty {}
      const needsTermsArtists = combinedArtists.filter(a => {
        // Must have accepted artwork
        if (!a.has_accepted_artwork) return false;
        
        const mapping = a.submission_mapping;
        if (!mapping) {
          console.log('Artist with accepted artwork but NO mapping:', a.email);
          return true; // They need terms but have no mapping record
        }
        
        // Check if terms_accepted is empty object (not accepted)
        const termsAccepted = mapping.terms_accepted;
        const hasAcceptedTerms = termsAccepted && typeof termsAccepted === 'object' && Object.keys(termsAccepted).length > 0;
        
        const qualifies = !hasAcceptedTerms;
        if (qualifies) {
          console.log('Needs terms artist:', a.email, 'has_accepted_artwork:', a.has_accepted_artwork, 'terms_accepted:', termsAccepted);
        }
        return qualifies;
      });
      
      console.log(`Total needsTerms artists: ${needsTermsArtists.length}`);

      const audienceData = {
        all_artists: combinedArtists,
        paid_artists: combinedArtists.filter(a => a.has_paid),
        unpaid_artists: allArtistsInSubmission.filter(a => !a.has_paid), // Only from submission mapping
        artists_with_artwork: combinedArtists.filter(a => a.has_submitted_artwork),
        no_artwork_submitted: allArtistsInSubmission.filter(a => a.has_paid && !a.has_submitted_artwork),
        accepted_artists: combinedArtists.filter(a => a.has_accepted_artwork),
        not_accepted: combinedArtists.filter(a => a.has_submitted_artwork && !a.has_accepted_artwork),
        needs_terms: needsTermsArtists,
        checked_in_artists: combinedArtists.filter(a => a.has_accepted_artwork && checkedInArtistUuids.has(a.uuid)),
        not_checked_in_artists: combinedArtists.filter(a => a.has_accepted_artwork && !checkedInArtistUuids.has(a.uuid))
      };

      // 8. Load artists with pending/emailed payouts for this collection
      let artistsPendingPayout = [];
      try {
        const { data: payoutRecords } = await supabase
          .from('artist_payout_records')
          .select('artist_uuid, amount, status')
          .eq('collection_id', collectionId)
          .in('status', ['pending', 'emailed']);

        if (payoutRecords?.length > 0) {
          const payoutByUuid = Object.fromEntries(payoutRecords.map(r => [r.artist_uuid, r]));
          const payoutUuids = payoutRecords.map(r => r.artist_uuid);
          const { data: payoutUsers } = await supabase
            .from('users')
            .select('uuid, first_name, last_name, email, nickname')
            .in('uuid', payoutUuids);
          artistsPendingPayout = (payoutUsers || []).map(u => ({
            ...u,
            payout_amount: payoutByUuid[u.uuid]?.amount || 0,
            payout_status: payoutByUuid[u.uuid]?.status || 'pending',
          }));
        }
        console.log(`Loaded ${artistsPendingPayout.length} artists with pending/emailed payouts`);
      } catch (payoutErr) {
        console.error('Error loading payout audience:', payoutErr);
      }

      audienceData.artists_pending_payout = artistsPendingPayout;

      console.log('Audience data:', {
        total: audienceData.all_artists.length,
        paid: audienceData.paid_artists.length,
        unpaid: audienceData.unpaid_artists.length,
        withArtwork: audienceData.artists_with_artwork.length,
        noArtwork: audienceData.no_artwork_submitted.length,
        accepted: audienceData.accepted_artists.length,
        needsTerms: audienceData.needs_terms.length
      });

      setSubmissionAudienceData(prev => ({
        ...prev,
        ...audienceData
      }));
      // Also update allArtists for backward compatibility
      setAllArtists(allArtistsInSubmission);
    } catch (err) {
      console.error('Error loading audience:', err);
    } finally {
      setAudienceLoading(false);
    }
  };

  // Load organization members for test mode selection (uses curator's org by default)
  const loadOrgMembers = async () => {
    if (!organizationId) return;
    loadOrgMembersForOrg(organizationId);
  };

  // Load organization members for a specific org (for collection's org)
  const loadOrgMembersForOrg = async (orgId) => {
    if (!orgId) return;
    
    try {
      const { data: members, error } = await supabase
        .from('organization_members')
        .select('id, user_uuid, role, users:user_uuid(first_name, last_name, email)')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .in('role', ['owner', 'admin', 'editor']);
      
      if (!error && members) {
        setOrgMembers(members);
        // Clear selected test admins when org changes
        setSelectedTestAdminIds([]);
        console.log(`Loaded ${members.length} org members for org ${orgId}`);
      }
    } catch (err) {
      console.error('Error loading org members:', err);
    }
  };

  // Load external organization contacts for invitation emails
  const loadOrganizationContacts = async (orgId) => {
    if (!orgId) return;
    
    try {
      console.log('Loading organization contacts for org:', orgId);
      const { data: contacts, error } = await supabase
        .from('external_organization_members')
        .select('id, first_name, last_name, email, membership_status')
        .eq('organization_id', orgId)
        .eq('membership_status', 'active');
      
      if (!error && contacts) {
        // Transform to match the format used by other audience types
        const formattedContacts = contacts.map(c => ({
          uuid: `contact_${c.id}`, // Use a prefixed ID since these aren't users yet
          contact_id: c.id,
          first_name: c.first_name,
          last_name: c.last_name,
          nickname: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
          email: c.email,
          is_organization_contact: true
        })).filter(c => c.email); // Only include contacts with email addresses
        
        console.log(`Loaded ${formattedContacts.length} organization contacts`);
        
        setSubmissionAudienceData(prev => ({
          ...prev,
          organization_contacts: formattedContacts
        }));
      }
    } catch (err) {
      console.error('Error loading organization contacts:', err);
    }
  };

  // Load organization contacts when that audience is selected or when org changes
  useEffect(() => {
    const collectionOrgId = selectedCollection?.organization_id || propCollectionData?.organization_id || organizationId;
    if (collectionOrgId) {
      loadOrganizationContacts(collectionOrgId);
    }
  }, [selectedCollection?.organization_id, propCollectionData?.organization_id, organizationId]);

  // Load platform-wide audiences (all users, prospects) - only when needed
  const loadPlatformAudiences = async () => {
    if (platformAudienceLoading) return;
    
    try {
      setPlatformAudienceLoading(true);
      console.log('Loading platform-wide audiences...');

      // Helper function to paginate through Supabase results (bypasses 1000 row limit)
      const fetchAllPaginated = async (tableName, selectCols, filters = []) => {
        const pageSize = 1000;
        let allData = [];
        let page = 0;
        let hasMore = true;
        
        while (hasMore) {
          let query = supabase.from(tableName).select(selectCols);
          
          // Apply filters
          for (const filter of filters) {
            if (filter.type === 'not') query = query.not(filter.col, filter.op, filter.val);
            else if (filter.type === 'or') query = query.or(filter.val);
            else if (filter.type === 'eq') query = query.eq(filter.col, filter.val);
            else if (filter.type === 'in') query = query.in(filter.col, filter.val);
          }
          
          const { data, error } = await query
            .order('created_at', { ascending: true })
            .range(page * pageSize, (page + 1) * pageSize - 1);
          
          if (error) throw error;
          
          allData = allData.concat(data || []);
          hasMore = (data || []).length === pageSize;
          page++;
        }
        
        return allData;
      };

      // Load all users with pagination
      const allUsersData = await fetchAllPaginated('users', 'uuid, email, first_name, last_name, nickname, test_account', [
        { type: 'not', col: 'email', op: 'is', val: null },
        { type: 'or', val: 'test_account.is.null,test_account.eq.false' }
      ]);

      console.log(`Loaded ${allUsersData.length} total users from database`);

      // Filter out vibrantcast.com and appleid.com emails (non-deliverable)
      const validUsers = (allUsersData || []).filter(user => {
        const email = (user.email || '').toLowerCase();
        // Exclude internal/test emails and Apple private relay (can't send to them)
        if (email.endsWith('@vibrantcast.com')) return false;
        if (email.endsWith('@appleid.com')) return false;
        if (email.endsWith('@privaterelay.appleid.com')) return false;
        if (email === '') return false;
        return true;
      });

      // Load all artists who have ever submitted to any collection
      const { data: artistMappings, error: artistError } = await supabase
        .from('artist_submission_mapping')
        .select('user_id')
        .not('user_id', 'is', null);
      
      if (artistError) throw artistError;
      
      // Get unique artist UUIDs
      const artistUuids = [...new Set((artistMappings || []).map(m => m.user_id))];
      
      // Filter validUsers to only include artists
      const allArtistsEver = validUsers.filter(user => artistUuids.includes(user.uuid));

      // Load all collectors (users with collector preferences)
      const { data: collectorPrefs, error: collectorError } = await supabase
        .from('collector_artwork_preferences')
        .select('user_uuid');
      
      if (collectorError) throw collectorError;
      
      // Get unique collector UUIDs
      const collectorUuids = [...new Set((collectorPrefs || []).map(p => p.user_uuid))];
      
      // Filter validUsers to only include collectors
      const allCollectors = validUsers.filter(user => collectorUuids.includes(user.uuid));

      // Load all prospects (with email addresses only)
      const { data: prospectsData, error: prospectsError } = await supabase
        .from('user_prospects')
        .select('*')
        .not('email', 'is', null)
        .neq('email', '')
        .order('email');

      if (prospectsError) throw prospectsError;

      // Transform prospects to match user format
      const formattedProspects = (prospectsData || []).map(p => ({
        uuid: p.uuid || `prospect_${p.id}`,
        prospect_id: p.id,
        email: p.email,
        first_name: p.first_name,
        last_name: p.last_name,
        nickname: `${p.first_name || ''} ${p.last_name || ''}`.trim() || null,
        is_prospect: true,
        is_linked: !!p.uuid
      }));

      // Build deduped audience of users + prospects that have NOT bought ticket for event 186.
      // Event 186 belongs to collection 349 (Festival of Independent Artists).
      const EVENT_NON_BUYERS_EVENT_ID = 186;
      const buyerUuidSet = new Set();
      const buyerEmailSet = new Set();
      const { data: eventTickets, error: eventTicketsError } = await supabase
        .from('user_event_tickets')
        .select('user_uuid, payment_status')
        .eq('event_id', EVENT_NON_BUYERS_EVENT_ID)
        .in('payment_status', ['completed', 'paid', 'succeeded', 'complete']);

      if (eventTicketsError) throw eventTicketsError;

      (eventTickets || []).forEach(t => {
        if (t.user_uuid) buyerUuidSet.add(t.user_uuid);
      });

      (allUsersData || []).forEach(user => {
        if (!buyerUuidSet.has(user.uuid)) return;
        const normalizedEmail = (user.email || '').trim().toLowerCase();
        if (normalizedEmail) buyerEmailSet.add(normalizedEmail);
      });

      const nonBuyerUsers = validUsers.filter(u => {
        const normalizedEmail = (u.email || '').trim().toLowerCase();
        return !buyerUuidSet.has(u.uuid) && !buyerEmailSet.has(normalizedEmail);
      });
      const event186NonBuyersMap = new Map();

      nonBuyerUsers.forEach(user => {
        const normalizedEmail = (user.email || '').trim().toLowerCase();
        if (!normalizedEmail) return;
        event186NonBuyersMap.set(normalizedEmail, {
          ...user,
          email: normalizedEmail
        });
      });

      formattedProspects.forEach(prospect => {
        const normalizedEmail = (prospect.email || '').trim().toLowerCase();
        if (!normalizedEmail) return;
        if (buyerEmailSet.has(normalizedEmail)) return;
        // Prefer canonical user records when both user and prospect share an email.
        if (event186NonBuyersMap.has(normalizedEmail)) return;
        event186NonBuyersMap.set(normalizedEmail, {
          ...prospect,
          email: normalizedEmail
        });
      });

      const event186NonBuyers = Array.from(event186NonBuyersMap.values());

      // Load VibrantCast artists with public artworks (for Portfolio Status emails)
      // These are artists who have at least one artwork with access_control = 'Public' and valid status
      let vcArtists = [];
      try {
        // Use pagination to get all artworks (bypasses 1000 row limit)
        const allArtworks = await fetchAllPaginated('vcs', 'uuid', [
          { type: 'eq', col: 'access_control', val: 'Public' },
          { type: 'in', col: 'status', val: ['For Sale', 'Not For Sale', 'Sold'] },
          { type: 'not', col: 'uuid', op: 'is', val: null }
        ]);
        
        const artworkArtistUuids = [...new Set(allArtworks.map(a => a.uuid))];
        console.log(`Found ${artworkArtistUuids.length} artists with public artworks`);
        vcArtists = validUsers.filter(user => artworkArtistUuids.includes(user.uuid));
        
        console.log(`Filtered to ${vcArtists.length} VC artists after excluding test/vibrantcast emails`);
      } catch (vcErr) {
        console.error('Error loading VC artists:', vcErr);
      }

      setPlatformAudiences({
        all_users: validUsers,
        all_artists_ever: allArtistsEver,
        all_collectors: allCollectors,
        prospects_all: formattedProspects,
        prospects_linked: formattedProspects.filter(p => p.is_linked),
        prospects_unlinked: formattedProspects.filter(p => !p.is_linked),
        vc_artists: vcArtists,
        event_186_non_buyers: event186NonBuyers
      });

      console.log(`Loaded ${validUsers.length} valid users, ${allArtistsEver.length} artists, ${allCollectors.length} collectors, ${formattedProspects.length} prospects, ${vcArtists.length} VC artists, ${event186NonBuyers.length} event-186 non-buyers`);

    } catch (err) {
      console.error('Error loading platform audiences:', err);
    } finally {
      setPlatformAudienceLoading(false);
    }
  };

  // Load platform audiences when selecting platform-wide audience types
  useEffect(() => {
    const platformAudienceTypes = ['all_users', 'all_artists_ever', 'all_collectors', 'prospects_all', 'prospects_linked', 'prospects_unlinked', 'vc_artists', 'event_186_non_buyers'];
    // Load if we don't have baseline data yet, or if a computed audience is requested and empty
    const needsLoad = platformAudiences.all_users.length === 0 || 
                      (targetAudience === 'vc_artists' && platformAudiences.vc_artists.length === 0) ||
                      (targetAudience === 'event_186_non_buyers' && platformAudiences.event_186_non_buyers.length === 0);
    if (platformAudienceTypes.includes(targetAudience) && needsLoad) {
      loadPlatformAudiences();
    }
  }, [targetAudience]);

  // Auto-select audience when communication type changes (based on template's audience_type)
  useEffect(() => {
    if (!selectedCommunication) return;
    
    // First check emailTemplates from database
    const template = emailTemplates.find(t => t.template_key === selectedCommunication);
    if (template?.audience_type) {
      // Map audience_type to targetAudience keys
      const audienceMapping = {
        'accepted_artists': 'accepted_artists',
        'no_artwork_submitted': 'no_artwork_submitted',
        'all_artists': 'all_artists',
        'not_accepted': 'not_accepted',
        'unpaid_artists': 'unpaid_artists',
        'organization_contacts': 'organization_contacts',
        'needs_terms': 'needs_terms',
        // Platform-wide audiences
        'all_users': 'all_users',
        'prospects': 'prospects_all',
        'prospects_all': 'prospects_all',
        'prospects_linked': 'prospects_linked',
        'prospects_unlinked': 'prospects_unlinked',
        // VibrantCast-specific
        'vc_artists': 'vc_artists',
        'event_186_non_buyers': 'event_186_non_buyers'
      };
      setTargetAudience(audienceMapping[template.audience_type] || 'all_artists');
      setExcludedUserIds(new Set());
      return;
    }
    
    // Fallback to hardcoded communicationOptions
    const allOptions = [...communicationOptions, ...platformCommunicationOptions, ...vibrantcastCommunicationOptions, orgContactsCommunicationOption, ...customCommunicationTypes];
    const selectedOption = allOptions.find(opt => opt.value === selectedCommunication);
    if (selectedOption) {
      setTargetAudience(selectedOption.audience);
      setExcludedUserIds(new Set());
    }
  }, [selectedCommunication, emailTemplates, customCommunicationTypes]);

  const loadCuratorData = async () => {
    try {
      console.log('Loading curator data...');
      setPageLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        setPageLoading(false);
        return;
      }

      // Use organization from context, or fetch from DB if not available
      let effectiveOrgId = contextOrganization?.organization_id;
      let effectiveOrg = contextOrganization;
      
      if (!effectiveOrgId) {
        // Get curator's organization membership
        const { data: orgMembership, error: orgMembershipError } = await supabase
          .from('organization_members')
          .select('organization_id, role, organizations(*)')
          .eq('user_uuid', user.id)
          .eq('is_active', true)
          .limit(1)
          .single();

        if (!orgMembershipError && orgMembership) {
          effectiveOrgId = orgMembership.organization_id;
          effectiveOrg = orgMembership.organizations;
          setOrganizationId(orgMembership.organization_id);
          setOrganization(orgMembership.organizations);
          console.log('Loaded organization from DB:', orgMembership.organizations?.name);
        }
      } else {
        setOrganizationId(effectiveOrgId);
        setOrganization(effectiveOrg);
        console.log('Using organization from context:', contextOrganization.name);
      }

      // If no organization, show empty state
      if (!effectiveOrgId) {
        setCollections([]);
        setPageLoading(false);
        return;
      }

      // Load collections for the dropdown selector (only if not in collection context)
      if (!isCollectionContext) {
        // Get collections for the selected organization
        const { data: collectionsData, error: collectionsError } = await supabase
          .from('collections')
          .select('collection_id, title, organization_id, organizations(name, contact_email)')
          .eq('organization_id', effectiveOrgId)
          .order('created_at', { ascending: false });
        
        if (collectionsError) {
          console.error('Collections load error:', collectionsError);
        } else if (collectionsData) {
          setCollections(collectionsData);
          console.log('Loaded', collectionsData.length, 'collections for org', effectiveOrgId);
          
          // Restore saved collection from localStorage if we have one
          const savedCollectionId = localStorage.getItem('communications_last_collection_id');
          if (savedCollectionId && !selectedCollection) {
            const savedColl = collectionsData.find(c => c.collection_id === parseInt(savedCollectionId, 10));
            if (savedColl) {
              setSelectedCollection(savedColl);
              setSelectedCollectionId(savedColl.collection_id);
              console.log('Restored saved collection:', savedColl.title);
              // Load organization contacts for this collection's org
              if (savedColl.organization_id) {
                loadOrganizationContacts(savedColl.organization_id);
              }
            }
          }
          
          // Also load organization contacts for current org if not loaded yet
          if (effectiveOrgId) {
            loadOrganizationContacts(effectiveOrgId);
          }
        }
      }

      setPageLoading(false);
    } catch (err) {
      console.error('Error loading curator data:', err);
      setError(err.message || 'Failed to load data');
      setPageLoading(false);
    }
  };

  // Load email templates from database (uses curator's org by default)
  const loadEmailTemplates = async () => {
    if (!organizationId) return;
    loadEmailTemplatesForOrg(organizationId);
  };

  // Load email templates for a specific org (for collection's org)
  const loadEmailTemplatesForOrg = async (orgId) => {
    if (!orgId) return;

    try {
      const { data: templates, error } = await supabase
        .from('organization_email_templates')
        .select('*')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('template_key');

      if (error) throw error;

      setEmailTemplates(templates || []);
      console.log(`Loaded ${templates?.length || 0} email templates for org ${orgId}`);
    } catch (err) {
      console.error('Error loading email templates:', err);
    }
  };

  // Save email template to database
  const saveEmailTemplate = async (template) => {
    if (!organizationId) {
      alert('No organization found. Please contact support.');
      return;
    }

    setSavingTemplate(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const templateData = {
        organization_id: organizationId,
        template_key: template.template_key,
        subject: template.subject,
        html_body: template.html_body,
        plain_text_body: template.plain_text_body || '',
        audience_type: template.audience_type,
        mandrill_template_name: template.mandrill_template_name || null,
        merge_vars: mergeVariables.reduce((acc, v) => {
          acc[v.key] = v.description;
          return acc;
        }, {}),
        created_by: user?.id,
        updated_at: new Date().toISOString()
      };

      // Check if template already exists
      const existingTemplate = emailTemplates.find(t => t.template_key === template.template_key);

      if (existingTemplate) {
        // Update existing
        const { error } = await supabase
          .from('organization_email_templates')
          .update(templateData)
          .eq('id', existingTemplate.id);

        if (error) throw error;
        console.log('Updated template:', template.template_key);
      } else {
        // Insert new
        const { error } = await supabase
          .from('organization_email_templates')
          .insert(templateData);

        if (error) throw error;
        console.log('Created template:', template.template_key);
      }

      // Reload templates
      await loadEmailTemplates();
      setEditingTemplate(null);
      setNewTemplate({
        template_key: '',
        subject: '',
        html_body: '',
        plain_text_body: '',
        audience_type: 'accepted_artists',
        mandrill_template_name: ''
      });

      alert('Template saved successfully!');
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Failed to save template: ' + err.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  // Delete email template
  const deleteEmailTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const { error } = await supabase
        .from('organization_email_templates')
        .update({ is_active: false })
        .eq('id', templateId);

      if (error) throw error;

      await loadEmailTemplates();
      alert('Template deleted successfully!');
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Failed to delete template: ' + err.message);
    }
  };

  // Use default template as starting point
  const useDefaultTemplate = (defaultTemplate) => {
    setNewTemplate({
      template_key: defaultTemplate.template_key,
      subject: defaultTemplate.subject,
      html_body: defaultTemplate.html_body,
      plain_text_body: '',
      audience_type: defaultTemplate.audience_type,
      mandrill_template_name: ''
    });
    setEditingTemplate(null);
  };

  // Edit existing template
  const startEditingTemplate = (template) => {
    setEditingTemplate(template);
    setNewTemplate({
      template_key: template.template_key,
      subject: template.subject,
      html_body: template.html_body || '',
      plain_text_body: template.plain_text_body || '',
      audience_type: template.audience_type,
      mandrill_template_name: template.mandrill_template_name || ''
    });
  };

  // Preview email with sample data - uses collection's organization
  // In test mode, show the selected admin's name; otherwise show sample artist name
  const getPreviewHtml = (htmlBody) => {
    if (!htmlBody) return '';
    
    const collectionName = selectedCollection?.title || propCollectionData?.title || 'Spring Art Exhibition 2025';
    // Use collection's organization, not curator's
    const collectionOrg = selectedCollection?.organizations || propCollectionData?.organizations || organization;
    const orgName = collectionOrg?.name || organization?.name || 'Art Organization';
    const orgEmail = collectionOrg?.contact_email || organization?.contact_email || 'info@example.org';
    
    // Check if we have a preview artist selected for portfolio emails
    let artistName = 'Jane Artist';
    let firstName = 'Jane';
    let portfolioSummaryHtml = '<div style="padding: 16px; background: #f3f4f6; border-radius: 8px; margin: 16px 0; text-align: center; color: #6b7280;">[Portfolio Summary will appear here]</div>';
    let artworkListHtml = '<div style="padding: 16px; background: #f3f4f6; border-radius: 8px; margin: 16px 0; text-align: center; color: #6b7280;">[Artwork List will appear here]</div>';
    let totalArtworks = '0';
    let forSaleCount = '0';
    let soldCount = '0';
    let printsAvailableCount = '0';
    let locatedCount = '0';
    
    if (previewArtistUuid && previewPortfolioData) {
      // Use real artist data
      const previewArtist = currentAudience.find(a => a.uuid === previewArtistUuid);
      if (previewArtist) {
        firstName = previewArtist.first_name || previewArtist.nickname?.split(' ')[0] || 'Artist';
        artistName = previewArtist.nickname || `${previewArtist.first_name || ''} ${previewArtist.last_name || ''}`.trim() || 'Artist';
      }
      
      // Generate real portfolio HTML
      portfolioSummaryHtml = generatePortfolioSummaryHtml(previewPortfolioData);
      artworkListHtml = generateArtworkListHtml(previewPortfolioData);
      totalArtworks = String(previewPortfolioData.total_artworks || 0);
      forSaleCount = String(previewPortfolioData.for_sale_count || 0);
      soldCount = String(previewPortfolioData.sold_count || 0);
      printsAvailableCount = String(previewPortfolioData.prints_available_count || 0);
      locatedCount = String(previewPortfolioData.located_count || 0);
    } else if (testMode && selectedTestAdminIds.length > 0) {
      const selectedAdmin = orgMembers.find(m => m.id === selectedTestAdminIds[0]);
      if (selectedAdmin?.users) {
        firstName = selectedAdmin.users.first_name || 'Test';
        const lastName = selectedAdmin.users.last_name || 'Admin';
        artistName = `${firstName} ${lastName}`.trim();
      }
    } else if (testMode && orgMembers.length > 0) {
      // If no specific admin selected but in test mode, use first admin
      const firstAdmin = orgMembers[0];
      if (firstAdmin?.users) {
        firstName = firstAdmin.users.first_name || 'Test';
        const lastName = firstAdmin.users.last_name || 'Admin';
        artistName = `${firstName} ${lastName}`.trim();
      }
    }
    
    let preview = htmlBody
      .replace(/\{artist_name\}/g, artistName)
      .replace(/\{first_name\}/g, firstName)
      .replace(/\{collection_name\}/g, collectionName)
      .replace(/\{submission_id\}/g, String(submissionDetails.submission_id || '13'))
      .replace(/\{submission_deadline\}/g, submissionDetails.deadline || 'December 31, 2025')
      .replace(/\{organization_name\}/g, orgName)
      .replace(/\{organization_logo\}/g, collectionOrg?.logo || organization?.logo || '')
      .replace(/\{contact_email\}/g, orgEmail)
      .replace(/\{accepted_artworks\}/g, '<ul><li>"Sunset Dreams" - Oil on Canvas, 24x36"</li><li>"Morning Light" - Acrylic, 18x24"</li></ul>')
      .replace(/\{important_dates\}/g, '<ul><li><strong>Exhibition:</strong> March 15-30, 2025</li><li><strong>Drop-off:</strong> March 12, 2025, 10am-4pm</li><li><strong>Reception:</strong> March 15, 2025, 6-9pm</li><li><strong>Pick-up:</strong> March 31, 2025, 10am-4pm</li></ul>')
      .replace(/\{acceptance_url\}/g, '#preview-acceptance-link')
      .replace(/\{event_readiness_url\}/g, 'https://vibrantcast.com/event-readiness?collectionId=349&eventId=186&artistUuid=preview-artist-uuid')
      .replace(/\{drop_off_date\}/g, 'March 12, 2025, 10am-4pm')
      .replace(/\{drop_off_location\}/g, '123 Gallery Street, Art City, AC 12345')
      .replace(/\{pickup_date\}/g, 'March 31, 2025, 10am-4pm')
      .replace(/\{pickup_location\}/g, '123 Gallery Street, Art City, AC 12345')
      // Portfolio merge variables
      .replace(/\{portfolio_summary\}/g, portfolioSummaryHtml)
      .replace(/\{artwork_list\}/g, artworkListHtml)
      .replace(/\{total_artworks\}/g, totalArtworks)
      .replace(/\{for_sale_count\}/g, forSaleCount)
      .replace(/\{sold_count\}/g, soldCount)
      .replace(/\{prints_available_count\}/g, printsAvailableCount)
      .replace(/\{located_count\}/g, locatedCount)
      .replace(/\{artist_studio_url\}/g, 'https://vibrantcast.com/artist-studio-dashboard')
      .replace(/\{apply_url\}/g, 'https://vibrantcast.com/apply')
      // Promo code preview (sample data)
      .replace(/\{promo_codes\}/g, '<ul><li><strong>AREND-20OFF-1</strong> — 20% off</li><li><strong>AREND-20OFF-2</strong> — 20% off</li><li><strong>AREND-100OFF-1</strong> — Free ticket</li></ul>')
      .replace(/\{promo_links\}/g, '<ul><li><a href="https://share.vibrantcast.com/event?id=186&promo=AREND-20OFF-1" style="color:#7c3aed">AREND-20OFF-1</a> — 20% off</li><li><a href="https://share.vibrantcast.com/event?id=186&promo=AREND-100OFF-1" style="color:#7c3aed">AREND-100OFF-1</a> — Free ticket</li></ul>')
      // Payout merge variables (sample preview values)
      .replace(/\{payout_amount\}/g, '$292.50')
      .replace(/\{payout_status\}/g, 'pending')
      .replace(/\{sales_total\}/g, '$416.43')
      .replace(/\{commission_pct\}/g, '30')
      .replace(/\{payout_line_items\}/g, '<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:13px;"><thead><tr style="background:#f9fafb;"><th style="text-align:left;padding:6px 8px;border-bottom:2px solid #e5e7eb;">Artwork</th><th style="text-align:left;padding:6px 8px;border-bottom:2px solid #e5e7eb;">Type</th><th style="text-align:right;padding:6px 8px;border-bottom:2px solid #e5e7eb;">Price</th></tr></thead><tbody><tr><td style="padding:5px 8px;border-bottom:1px solid #f3f4f6;">Sunset Dreams</td><td style="padding:5px 8px;border-bottom:1px solid #f3f4f6;color:#6b7280;">original</td><td style="padding:5px 8px;border-bottom:1px solid #f3f4f6;text-align:right;font-weight:600;">$295.00</td></tr><tr><td style="padding:5px 8px;border-bottom:1px solid #f3f4f6;">Morning Light (Print)</td><td style="padding:5px 8px;border-bottom:1px solid #f3f4f6;color:#6b7280;">print</td><td style="padding:5px 8px;border-bottom:1px solid #f3f4f6;text-align:right;font-weight:600;">$121.43</td></tr></tbody></table>');
    
    return preview;
  };

  // Get email content for the selected communication type
  const getSelectedEmailContent = () => {
    if (!selectedCommunication) return null;
    
    const collectionName = selectedCollection?.title || propCollectionData?.title || 'Your Collection';
    
    // If user has composed content, use that (this is what will actually be sent)
    if (composeSubject || composeBody) {
      return {
        subject: composeSubject.replace(/\{collection_name\}/g, collectionName),
        html_body: normalizeSpaces(composeBody),
        template_name: null
      };
    }
    
    // Check for custom template first
    const customTemplate = emailTemplates.find(t => t.template_key === selectedCommunication);
    if (customTemplate) {
      return {
        subject: customTemplate.subject.replace(/\{collection_name\}/g, collectionName),
        html_body: customTemplate.html_body,
        template_name: customTemplate.mandrill_template_name || null
      };
    }
    
    // Fall back to default templates
    const defaultTemplate = defaultEmailTemplates.find(t => t.template_key === selectedCommunication);
    if (defaultTemplate) {
      return {
        subject: defaultTemplate.subject.replace(/\{collection_name\}/g, collectionName),
        html_body: defaultTemplate.html_body,
        template_name: null
      };
    }
    
    // For built-in communication options without custom templates
    const allOptions = [...communicationOptions, ...platformCommunicationOptions, orgContactsCommunicationOption, ...customCommunicationTypes];
    const selectedOption = allOptions.find(opt => opt.value === selectedCommunication);
    if (selectedOption) {
      return {
        subject: `${selectedOption.label} - ${collectionName}`,
        html_body: `<p>This communication will be sent using the "${selectedOption.label}" template.</p><p>${selectedOption.description}</p>`,
        template_name: selectedOption.email_template
      };
    }
    
    return null;
  };

  // Get recipients display for email preview
  const getRecipientsDisplay = () => {
    if (testMode) {
      const selectedAdmins = orgMembers.filter(m => selectedTestAdminIds.includes(m.id));
      if (selectedAdmins.length > 0) {
        return selectedAdmins.map(m => ({
          name: m.users?.first_name ? `${m.users.first_name} ${m.users.last_name || ''}`.trim() : 'Team Member',
          email: m.users?.email || 'no-email',
          role: m.role
        }));
      }
      // If no specific admins selected, show all org admins
      return orgMembers.filter(m => ['owner', 'admin'].includes(m.role)).map(m => ({
        name: m.users?.first_name ? `${m.users.first_name} ${m.users.last_name || ''}`.trim() : 'Team Member',
        email: m.users?.email || 'no-email',
        role: m.role
      }));
    }
    
    // Production mode - show actual recipients
    return currentAudience.slice(0, 5).map(a => ({
      name: a.nickname || `${a.first_name} ${a.last_name}`.trim(),
      email: a.email,
      role: 'artist'
    }));
  };

  // Create a new communication type
  const createCommunicationType = () => {
    if (!newType.title || !newType.description || !newType.emailSubject || !newType.pushTitle) {
      alert('Please fill in all fields');
      return;
    }

    const type = {
      value: `custom_${Date.now()}`,
      label: newType.title,
      description: newType.description,
      audience: newType.audience,
      email_template: 'custom',
      color: newType.color,
      push: {
        title: newType.pushTitle,
        message: newType.pushMessage || 'New message from {collection_name}',
        category: 'custom_communication'
      }
    };

    setCustomCommunicationTypes([...customCommunicationTypes, type]);
    setNewType({
      title: '',
      description: '',
      audience: 'all_artists',
      emailSubject: '',
      pushTitle: '',
      pushMessage: '',
      color: '#7c3aed'
    });
    setShowCreateForm(false);
    alert('Communication type created! Now go to "Send" tab to use it.');
  };

  // Delete a custom communication type
  const deleteCommunicationType = (typeId) => {
    setCustomCommunicationTypes(customCommunicationTypes.filter(t => t.value !== typeId));
  };

  // Use a suggested type as a template
  const useTemplate = (template) => {
    setNewType({
      title: template.title,
      description: template.description,
      audience: template.defaultAudience,
      emailSubject: template.defaultEmailSubject,
      pushTitle: template.defaultPushTitle,
      pushMessage: `{collection_name}`,
      color: template.color
    });
    setShowCreateForm(true);
  };

  // Calculate audience statistics - using submission data when available, plus platform-wide audiences
  const audienceStats = {
    // Collection-based audiences
    all_artists: submissionAudienceData.all_artists.length,
    accepted_artists: submissionAudienceData.accepted_artists.length,
    unpaid_artists: submissionAudienceData.unpaid_artists.length,
    no_artwork_submitted: submissionAudienceData.no_artwork_submitted.length,
    not_accepted: submissionAudienceData.not_accepted?.length || 0,
    needs_terms: submissionAudienceData.needs_terms?.length || 0,
    checked_in_artists: submissionAudienceData.checked_in_artists?.length || 0,
    not_checked_in_artists: submissionAudienceData.not_checked_in_artists?.length || 0,
    organization_contacts: submissionAudienceData.organization_contacts?.length || 0,
    artists_pending_payout: submissionAudienceData.artists_pending_payout?.length || 0,
    single_artist: singleArtist ? 1 : 0,
    // Platform-wide audiences (not collection-specific)
    all_users: platformAudiences.all_users.length,
    all_artists_ever: platformAudiences.all_artists_ever.length,
    all_collectors: platformAudiences.all_collectors.length,
    prospects_all: platformAudiences.prospects_all.length,
    prospects_linked: platformAudiences.prospects_linked.length,
    prospects_unlinked: platformAudiences.prospects_unlinked.length,
    vc_artists: platformAudiences.vc_artists.length,
    event_186_non_buyers: platformAudiences.event_186_non_buyers.length
  };

  // Check if current audience is platform-wide (doesn't require collection)
  const isPlatformAudience = ['all_users', 'all_artists_ever', 'all_collectors', 'prospects_all', 'prospects_linked', 'prospects_unlinked', 'vc_artists', 'event_186_non_buyers'].includes(targetAudience);

  // Get artists in selected collection for exclusion filter
  const collectionArtistUuids = useMemo(() => {
    return new Set(submissionAudienceData.all_artists.map(a => a.uuid));
  }, [submissionAudienceData.all_artists]);

  // Preview audience function - using submission data for collection audiences, platform data for global
  const previewAudience = () => {
    // Single artist mode takes priority
    if (targetAudience === 'single_artist' && singleArtist) {
      return [singleArtist];
    }
    
    let audience = [];
    
    switch (targetAudience) {
      // Platform-wide audiences
      case 'all_users':
        audience = platformAudiences.all_users;
        break;
      case 'all_artists_ever':
        audience = platformAudiences.all_artists_ever;
        break;
      case 'all_collectors':
        audience = platformAudiences.all_collectors;
        break;
      case 'prospects_all':
        audience = platformAudiences.prospects_all;
        break;
      case 'prospects_linked':
        audience = platformAudiences.prospects_linked;
        break;
      case 'prospects_unlinked':
        audience = platformAudiences.prospects_unlinked;
        break;
      case 'vc_artists':
        audience = platformAudiences.vc_artists;
        break;
      case 'event_186_non_buyers':
        audience = platformAudiences.event_186_non_buyers;
        break;
      // Collection-based audiences
      case 'accepted_artists':
        audience = submissionAudienceData.accepted_artists;
        break;
      case 'unpaid_artists':
        audience = submissionAudienceData.unpaid_artists;
        break;
      case 'no_artwork_submitted':
        audience = submissionAudienceData.no_artwork_submitted;
        break;
      case 'not_accepted':
        audience = submissionAudienceData.not_accepted || [];
        break;
      case 'needs_terms':
        audience = submissionAudienceData.needs_terms || [];
        break;
      case 'checked_in_artists':
        audience = submissionAudienceData.checked_in_artists || [];
        break;
      case 'not_checked_in_artists':
        audience = submissionAudienceData.not_checked_in_artists || [];
        break;
      case 'organization_contacts':
        audience = submissionAudienceData.organization_contacts || [];
        break;
      case 'artists_pending_payout':
        audience = submissionAudienceData.artists_pending_payout || [];
        break;
      case 'all_artists':
      default:
        audience = submissionAudienceData.all_artists;
        break;
    }
    
    // Apply exclusion filter if enabled and collection is selected
    if (excludeCollectionArtists && selectedCollectionId && collectionArtistUuids.size > 0) {
      audience = audience.filter(a => !collectionArtistUuids.has(a.uuid));
    }
    
    return audience;
  };

  // Auto-select target audience when template is selected
  useEffect(() => {
    if (selectedCommunication) {
      const template = emailTemplates.find(t => t.template_key === selectedCommunication);
      if (template && template.audience_type) {
        setTargetAudience(template.audience_type);
      }
    }
  }, [selectedCommunication, emailTemplates]);

  // Populate compose fields when template is selected
  useEffect(() => {
    if (selectedCommunication) {
      const template = emailTemplates.find(t => t.template_key === selectedCommunication);
      console.log('Selected template:', selectedCommunication, template);
      if (template) {
        // Database columns are 'subject' and 'html_body'
        setComposeSubject(template.subject || template.email_subject || '');
        setComposeBody(template.html_body || template.email_html_body || '');
        console.log('Setting body:', template.html_body ? 'html_body exists' : 'html_body is empty', template.email_html_body ? 'email_html_body exists' : 'email_html_body is empty');
      } else {
        // Clear compose fields if no template found (custom/platform option)
        setComposeSubject('');
        setComposeBody('');
      }
      // Always show the compose editor when a communication is selected
      setShowComposeEditor(true);
    } else {
      setComposeSubject('');
      setComposeBody('');
    }
  }, [selectedCommunication, emailTemplates]);

  // Fetch portfolio data for preview artist
  const fetchPreviewPortfolioData = async (artistUuid) => {
    if (!artistUuid) {
      setPreviewPortfolioData(null);
      return;
    }
    
    setLoadingPreviewData(true);
    try {
      const { data, error } = await supabase.rpc('get_artist_portfolio_data', {
        artist_uuid: artistUuid
      });
      
      if (error) throw error;
      setPreviewPortfolioData(data);
    } catch (err) {
      console.error('Error fetching preview portfolio data:', err);
      setPreviewPortfolioData(null);
    } finally {
      setLoadingPreviewData(false);
    }
  };

  // Load portfolio data when preview artist changes
  useEffect(() => {
    if (previewArtistUuid && showSendEmailPreview) {
      fetchPreviewPortfolioData(previewArtistUuid);
    }
  }, [previewArtistUuid, showSendEmailPreview]);

  // Generate portfolio summary HTML (same as edge function)
  const generatePortfolioSummaryHtml = (portfolio) => {
    if (!portfolio || portfolio.total_artworks === 0) {
      return `<div style="padding: 20px; background: #fef3c7; border-radius: 8px; margin: 16px 0;">
        <p style="margin: 0; color: #92400e;">📭 <strong>No public artworks found.</strong></p>
        <p style="margin: 8px 0 0 0; color: #92400e;">Upload your artwork to be featured in the Artist Directory!</p>
      </div>`;
    }

    return `<div style="padding: 16px; background: #ecfdf5; border-radius: 8px; margin: 16px 0;">
      <h3 style="margin: 0 0 12px 0; color: #047857;">📊 Your Portfolio Summary</h3>
      <div style="display: flex; gap: 20px; flex-wrap: wrap;">
        <div style="text-align: center;">
          <div style="font-size: 24px; font-weight: bold; color: #111827;">${portfolio.total_artworks}</div>
          <div style="font-size: 12px; color: #6b7280;">Total Artworks</div>
        </div>
        <div style="text-align: center;">
          <div style="font-size: 24px; font-weight: bold; color: #059669;">${portfolio.for_sale_count}</div>
          <div style="font-size: 12px; color: #6b7280;">For Sale</div>
        </div>
        <div style="text-align: center;">
          <div style="font-size: 24px; font-weight: bold; color: #dc2626;">${portfolio.sold_count}</div>
          <div style="font-size: 12px; color: #6b7280;">Sold</div>
        </div>
        <div style="text-align: center;">
          <div style="font-size: 24px; font-weight: bold; color: #7c3aed;">${portfolio.prints_available_count}</div>
          <div style="font-size: 12px; color: #6b7280;">Prints Available</div>
        </div>
        <div style="text-align: center;">
          <div style="font-size: 24px; font-weight: bold; color: #2563eb;">${portfolio.located_count}</div>
          <div style="font-size: 12px; color: #6b7280;">At Galleries</div>
        </div>
      </div>
    </div>`;
  };

  // Generate artwork list HTML (same as edge function)
  const generateArtworkListHtml = (portfolio) => {
    if (!portfolio || !portfolio.artworks || portfolio.artworks.length === 0) {
      return '';
    }

    const artworkRows = portfolio.artworks.slice(0, 10).map(artwork => {
      const statusColor = artwork.status === 'For Sale' ? '#059669' : 
                         artwork.status === 'Sold' ? '#dc2626' : '#6b7280';
      const priceText = artwork.price ? `$${artwork.price.toLocaleString()}` : 'Price on request';
      const printsText = (artwork.prints_enabled || artwork.on_demand_enabled) 
        ? 'Prints available' : 'No Prints Available';
      const locationText = artwork.location_name 
        ? `📍 ${artwork.location_name}` : '';

      return `<tr style="border-bottom: 1px solid #e5e7eb;">
        <td style="padding: 12px 8px; vertical-align: top;">
          <strong style="color: #111827;">${artwork.title}</strong>
          <div style="font-size: 12px; color: #6b7280; margin-top: 4px;">
            <span style="color: ${statusColor}; font-weight: 500;">${artwork.status}</span>
            ${artwork.status === 'For Sale' ? ` • ${priceText}` : ''}
          </div>
        </td>
        <td style="padding: 12px 8px; vertical-align: top; font-size: 12px; color: #6b7280;">
          ${printsText}
        </td>
        <td style="padding: 12px 8px; vertical-align: top; font-size: 12px; color: #6b7280;">
          ${locationText}
        </td>
      </tr>`;
    }).join('');

    const moreText = portfolio.artworks.length > 10 
      ? `<p style="font-size: 12px; color: #6b7280; text-align: center; margin-top: 8px;">
          ...and ${portfolio.artworks.length - 10} more artworks
         </p>` 
      : '';

    return `<table style="width: 100%; border-collapse: collapse; margin: 16px 0;">
      <thead>
        <tr style="background: #f3f4f6;">
          <th style="padding: 8px; text-align: left; font-size: 12px; color: #374151;">Artwork</th>
          <th style="padding: 8px; text-align: left; font-size: 12px; color: #374151;">Prints</th>
          <th style="padding: 8px; text-align: left; font-size: 12px; color: #374151;">Public Location</th>
        </tr>
      </thead>
      <tbody>
        ${artworkRows}
      </tbody>
    </table>
    ${moreText}`;
  };

  // Filter out excluded users from audience
  const currentAudience = previewAudience().filter(artist => !excludedUserIds.has(artist.uuid));

  // When Skip Already Sent is on, look up how many of the current audience were already sent this subject
  useEffect(() => {
    if (!skipAlreadySent || !composeSubject?.trim()) {
      setAlreadySentCount(null);
      setCheckingSentCount(false);
      return;
    }
    if (currentAudience.length === 0) {
      setAlreadySentCount(0);
      setCheckingSentCount(false);
      return;
    }
    let cancelled = false;
    setCheckingSentCount(true);
    const lookup = async () => {
      try {
        const subj = composeSubject.trim();
        console.log('[Skip Already Sent] Checking email_logs for subject:', subj, 'audience size:', currentAudience.length);
        // Get emails of current audience
        const audienceEmails = new Set(currentAudience.map(a => (a.email || '').toLowerCase()).filter(Boolean));
        if (audienceEmails.size === 0) { 
          if (!cancelled) { setAlreadySentCount(0); setCheckingSentCount(false); }
          return; 
        }
        // Paginate email_logs for this subject
        const PAGE = 1000;
        let sentEmails = new Set();
        let from = 0;
        while (true) {
          const { data, error } = await supabase
            .from('email_logs')
            .select('user_email')
            .eq('subject', subj)
            .range(from, from + PAGE - 1);
          if (error) {
            console.error('[Skip Already Sent] Query error:', error);
            break;
          }
          if (!data || data.length === 0) break;
          data.forEach(r => sentEmails.add((r.user_email || '').toLowerCase()));
          console.log('[Skip Already Sent] Fetched', sentEmails.size, 'sent emails so far');
          if (data.length < PAGE) break;
          from += PAGE;
        }
        if (cancelled) return;
        // Count overlap
        let overlap = 0;
        audienceEmails.forEach(e => { if (sentEmails.has(e)) overlap++; });
        console.log('[Skip Already Sent] Found', overlap, 'already sent out of', audienceEmails.size, 'audience');
        setAlreadySentCount(overlap);
        setCheckingSentCount(false);
      } catch (err) {
        console.error('[Skip Already Sent] Error:', err);
        if (!cancelled) { setAlreadySentCount(null); setCheckingSentCount(false); }
      }
    };
    // Debounce slightly so we don't fire on every keystroke
    const timer = setTimeout(lookup, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [skipAlreadySent, composeSubject, currentAudience.length]);
  
  // Full audience before exclusions (for the modal)
  const fullAudience = previewAudience();

  // Send communication function
  const sendCommunication = async () => {
    if (!selectedCommunication) {
      alert('Please select a communication type');
      return;
    }

    // Require subject and body
    if (!composeSubject || !composeSubject.trim()) {
      alert('Please enter an email subject');
      return;
    }
    if (!composeBody || !composeBody.trim()) {
      alert('Please enter email content');
      return;
    }

    // Require collection selection when not in collection context AND not using platform/org-wide audience
    const collectionToUse = selectedCollectionId || propCollectionId;
    const currentIsPlatformAudience = ['all_users', 'all_artists_ever', 'all_collectors', 'prospects_all', 'prospects_linked', 'prospects_unlinked', 'organization_contacts', 'vc_artists', 'event_186_non_buyers'].includes(targetAudience);
    if (!collectionToUse && !isCollectionContext && !currentIsPlatformAudience) {
      alert('Please select a collection first');
      return;
    }

    setIsLoading(true);
    setResults(null);

    // Show sending status with recipient count
    const recipientCount = testMode ? 'test admins' : `${currentAudience.length} recipient${currentAudience.length !== 1 ? 's' : ''}`;
    setSendingStatus(`Preparing to send to ${recipientCount}...`);

    try {
      const allOptions = [...communicationOptions, ...platformCommunicationOptions, orgContactsCommunicationOption, ...customCommunicationTypes];
      const selectedOption = allOptions.find(opt => opt.value === selectedCommunication);

      // Check if we have a custom template for this communication type
      const customTemplate = emailTemplates.find(t => t.template_key === selectedCommunication);
      
      // Use organization_email_hub for custom templates, collection_communication_hub for legacy
      const useNewHub = customTemplate || organizationId;
      const endpoint = useNewHub 
        ? 'https://api.vibrantcast.com/functions/v1/organization_email_hub'
        : 'https://api.vibrantcast.com/functions/v1/collection_communication_hub';

      // Use the collection's organization_id, not the curator's
      const collectionOrgId = selectedCollection?.organization_id || propCollectionData?.organization_id || organizationId;
      
      // Convert excluded user IDs Set to array for payload
      const excludedIds = excludedUserIds.size > 0 ? Array.from(excludedUserIds) : undefined;
      
      // Build target_users list - handle organization contacts specially
      const targetUsers = currentAudience.map(a => {
        if (a.is_organization_contact) {
          return { 
            contact_id: a.contact_id,
            email: a.email, 
            first_name: a.first_name, 
            nickname: a.nickname || `${a.first_name || ''} ${a.last_name || ''}`.trim(),
            is_organization_contact: true
          };
        }
        // For prospects, include prospect_id
        if (a.is_prospect) {
          return { 
            prospect_id: a.prospect_id,
            email: a.email, 
            first_name: a.first_name, 
            nickname: a.nickname,
            is_prospect: true
          };
        }
        return { uuid: a.uuid, email: a.email, first_name: a.first_name, nickname: a.nickname, ...(a.payout_amount != null ? { payout_amount: `$${Number(a.payout_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` } : {}) };
      });
      
      const payload = useNewHub ? {
        organization_id: collectionOrgId || organizationId,
        collection_id: collectionToUse || null, // Optional for platform audiences
        template_key: selectedCommunication,
        target_audience: targetAudience,
        target_users: targetUsers,
        test_mode: testMode,
        test_admin_ids: testMode && selectedTestAdminIds.length > 0 ? selectedTestAdminIds : undefined,
        excluded_user_ids: excludedIds,
        custom_merge_vars: {},
        event_details: selectedCommunication === 'event_announcement' ? eventDetails : undefined,
        is_platform_audience: currentIsPlatformAudience,
        // Include composed email content (allows editing before send)
        email_subject: composeSubject || undefined,
        email_html_body: normalizeSpaces(composeBody) || undefined,
        skip_already_sent: skipAlreadySent || undefined
      } : {
        collection_id: collectionToUse,
        communication_type: selectedCommunication,
        target_audience: targetAudience,
        email_template: selectedOption?.email_template,
        push_notification: selectedOption?.push,
        test_mode: testMode,
        custom_message: customMessage,
        event_details: selectedCommunication === 'event_announcement' ? eventDetails : undefined,
        is_system_wide: true,
        target_artists: currentAudience.map(a => a.uuid)
      };

      console.log('Sending communication:', { endpoint, payloadUserCount: targetUsers.length });

      // Chunk large sends into multiple API calls to avoid edge function timeout
      // Each edge function invocation handles up to CHUNK_SIZE users
      const CHUNK_SIZE = 100;
      const needsChunking = useNewHub && !testMode && targetUsers.length > CHUNK_SIZE;

      if (needsChunking) {
        // Split into multiple API calls
        const totalChunks = Math.ceil(targetUsers.length / CHUNK_SIZE);
        const aggregatedResults = { total_users: targetUsers.length, email_sent: 0, skipped: 0, errors: [] };
        let lastResult = null;

        for (let i = 0; i < targetUsers.length; i += CHUNK_SIZE) {
          const chunk = targetUsers.slice(i, i + CHUNK_SIZE);
          const chunkNum = Math.floor(i / CHUNK_SIZE) + 1;
          setSendingStatus(`Sending batch ${chunkNum}/${totalChunks} (${chunk.length} recipients)... ${aggregatedResults.email_sent} sent so far`);

          const chunkPayload = { ...payload, target_users: chunk };
          try {
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(chunkPayload)
            });
            const chunkResult = await response.json();
            if (chunkResult.results) {
              aggregatedResults.email_sent += chunkResult.results.email_sent || 0;
              aggregatedResults.skipped += chunkResult.results.skipped || 0;
              if (chunkResult.results.errors?.length) {
                aggregatedResults.errors.push(...chunkResult.results.errors);
              }
            }
            lastResult = chunkResult;
          } catch (chunkErr) {
            console.error(`Chunk ${chunkNum} failed:`, chunkErr);
            aggregatedResults.errors.push(`Batch ${chunkNum} failed: ${chunkErr.message}`);
          }
        }

        // Build final result
        setResults({
          success: true,
          organization: lastResult?.organization,
          template_used: lastResult?.template_used,
          template_key: lastResult?.template_key,
          category_used: lastResult?.category_used,
          results: aggregatedResults
        });
      } else {
        // Single call for small audiences or test mode
        const recipientLabel = testMode ? 'test admins' : `${currentAudience.length} recipients`;
        setSendingStatus(`Sending emails to ${recipientLabel}...`);

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const result = await response.json();
        setResults(result);
      }
    } catch (error) {
      console.error('Error sending communication:', error);
      setResults({ success: false, error: error.message });
    } finally {
      setIsLoading(false);
      setSendingStatus('');
    }
  };

  // Loading state
  if (pageLoading) {
    return (
      <div style={{ backgroundColor: '#f9fafb', minHeight: '500px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
        <div className="spinner"></div>
        <p>Loading your communications...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ backgroundColor: '#f9fafb', minHeight: '500px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', color: '#dc2626', padding: '2rem' }}>
        <AlertTriangle size={48} />
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Error</h3>
        <p style={{ margin: '0.5rem 0', textAlign: 'center' }}>{error}</p>
        <button onClick={loadCuratorData} style={{ padding: '0.5rem 1rem', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  // Tab 3: Email Analytics
  if (activeTab === 'analytics') {
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #e5e7eb' }}>
        {/* Tab header */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem', flexWrap: 'wrap' }}>
          <button onClick={() => setActiveTab('send')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer' }}>
            Send Communications
          </button>
          <button onClick={() => setActiveTab('templates')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer' }}>
            Email Templates
          </button>
          <button onClick={() => setActiveTab('analytics')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '600', color: '#7c3aed', cursor: 'pointer', borderBottom: '2px solid #7c3aed', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <BarChart3 style={{ height: '1rem', width: '1rem' }} />
            Email Analytics
          </button>
          <button onClick={() => setActiveTab('media')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Image style={{ height: '1rem', width: '1rem' }} />
            Media Library
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <BarChart3 style={{ height: '1.25rem', width: '1.25rem', color: '#7c3aed' }} />
            Email Analytics
            {organization && <span style={{ fontSize: '0.875rem', fontWeight: '400', color: '#6b7280' }}>for {organization.name}</span>}
          </h3>
        </div>

        {organizationId ? (
          <EmailAnalytics 
            organizationId={organizationId} 
            collectionId={selectedCollectionId || null} 
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            <p>Organization not found. Please reload the page.</p>
          </div>
        )}
      </div>
    );
  }

  // Tab 4: Media Library
  if (activeTab === 'media') {
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #e5e7eb' }}>
        {/* Tab header */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem', flexWrap: 'wrap' }}>
          <button onClick={() => setActiveTab('send')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer' }}>
            Send Communications
          </button>
          <button onClick={() => setActiveTab('templates')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer' }}>
            Email Templates
          </button>
          <button onClick={() => setActiveTab('analytics')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <BarChart3 style={{ height: '1rem', width: '1rem' }} />
            Email Analytics
          </button>
          <button onClick={() => setActiveTab('media')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '600', color: '#7c3aed', cursor: 'pointer', borderBottom: '2px solid #7c3aed', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Image style={{ height: '1rem', width: '1rem' }} />
            Media Library
          </button>
        </div>

        {organizationId ? (
          <MediaLibraryTab 
            organizationId={organizationId} 
            organizationName={organization?.name}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            <p>Organization not found. Please reload the page.</p>
          </div>
        )}
      </div>
    );
  }

  // Tab 3: Email Templates Management
  if (activeTab === 'templates') {
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #e5e7eb' }}>
        {/* Tab header */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem', flexWrap: 'wrap' }}>
          <button onClick={() => setActiveTab('send')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer' }}>
            Send Communications
          </button>
          <button onClick={() => setActiveTab('templates')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '600', color: '#7c3aed', cursor: 'pointer', borderBottom: '2px solid #7c3aed' }}>
            Email Templates
          </button>
          <button onClick={() => setActiveTab('analytics')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <BarChart3 style={{ height: '1rem', width: '1rem' }} />
            Email Analytics
          </button>
          <button onClick={() => setActiveTab('media')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Image style={{ height: '1rem', width: '1rem' }} />
            Media Library
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FileText style={{ height: '1.25rem', width: '1.25rem', color: '#7c3aed' }} />
            Email Templates
            {organization && <span style={{ fontSize: '0.875rem', fontWeight: '400', color: '#6b7280' }}>for {organization.name}</span>}
          </h3>
          <button
            onClick={() => navigate('/curator/communications/templates/new')}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}
          >
            <Plus size={16} /> New Template
          </button>
        </div>

        {/* Existing Templates */}
        <div>
          <h4 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
            {emailTemplates.length > 0 ? 'YOUR EMAIL TEMPLATES' : 'NO TEMPLATES YET'}
          </h4>
          {emailTemplates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem', color: '#6b7280' }}>
              <FileText size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
              <p>No custom email templates yet. Click "New Template" to create your first one.</p>
              <p style={{ fontSize: '0.875rem' }}>You can also load default CAVA templates below.</p>
              <button
                onClick={async () => {
                  // Load default CAVA templates
                  for (const template of defaultEmailTemplates) {
                    await saveEmailTemplate({
                      ...template,
                      id: null
                    });
                  }
                }}
                disabled={savingTemplate}
                style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: savingTemplate ? 'not-allowed' : 'pointer' }}
              >
                {savingTemplate ? 'Loading...' : 'Load CAVA Default Templates'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
              {emailTemplates.map((template) => (
                <div key={template.id} style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem', backgroundColor: template.is_active ? 'white' : '#f9fafb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div>
                      <h5 style={{ fontWeight: '600', color: '#111827', margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {template.template_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        {!template.is_active && <span style={{ fontSize: '0.625rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>INACTIVE</span>}
                      </h5>
                      <p style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#6b7280', margin: 0 }}>{template.template_key}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button
                        onClick={() => navigate(`/curator/communications/templates/${template.id}/edit`)}
                        style={{ padding: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={async () => {
                          if (confirm('Delete this template?')) {
                            const { error } = await supabase.from('organization_email_templates').delete().eq('id', template.id);
                            if (!error) loadEmailTemplates();
                          }
                        }}
                        style={{ padding: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#374151', margin: '0 0 0.5rem 0', fontStyle: 'italic' }}>"{template.subject}"</p>
                  <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.75rem' }}>
                    <span style={{ backgroundColor: '#e5e7eb', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>
                      {template.audience_type?.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Tab 1: Send Communications (original tab)
  return (
    <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #e5e7eb' }}>
      {/* Tab header */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem', flexWrap: 'wrap' }}>
        <button onClick={() => setActiveTab('send')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '600', color: '#7c3aed', cursor: 'pointer', borderBottom: '2px solid #7c3aed' }}>
          Send Communications
        </button>
        <button onClick={() => setActiveTab('templates')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer' }}>
          Email Templates
        </button>
        <button onClick={() => setActiveTab('analytics')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <BarChart3 style={{ height: '1rem', width: '1rem' }} />
          Email Analytics
        </button>
        <button onClick={() => setActiveTab('media')} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', border: 'none', fontSize: '1rem', fontWeight: '500', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Image style={{ height: '1rem', width: '1rem' }} />
          Media Library
        </button>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Send style={{ height: '1.25rem', width: '1.25rem', color: '#7c3aed' }} />
          Send Communications
        </h3>
      </div>

      {/* Collection Selector - only show when not embedded in a collection page */}
      {!isCollectionContext && (
        <div style={{ marginBottom: '1.5rem' }}>
          {/* Show selected collection OR search interface */}
          {selectedCollection ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              background: '#f0f9ff',
              border: '2px solid #007bff',
              borderRadius: '12px',
              marginBottom: '1rem'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                background: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FileText size={24} color="#007bff" />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827' }}>
                  {selectedCollection.title}
                </h3>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: '#6c757d' }}>
                  Selected collection for communications
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedCollection(null);
                  setSelectedCollectionId(null);
                  setCollectionSearch('');
                  localStorage.removeItem('communications_last_collection_id');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: '#495057'
                }}
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#0369a1', marginBottom: '0.5rem' }}>
                SELECT COLLECTION
              </label>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                Search for a collection to send communications to its artists
              </p>

              {/* Search input */}
              <div style={{ position: 'relative', marginBottom: '1rem' }}>
                <Search 
                  size={18} 
                  style={{ 
                    position: 'absolute', 
                    left: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }} 
                />
                <input
                  type="text"
                  placeholder="Start typing to search collections..."
                  value={collectionSearch}
                  onChange={(e) => setCollectionSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 40px',
                    border: '2px solid #bae6fd',
                    borderRadius: '8px',
                    fontSize: '0.9375rem',
                    background: '#f0f9ff'
                  }}
                />
              </div>

              {/* Collection cards */}
              {!collectionSearch.trim() ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d', background: '#f9fafb', borderRadius: '8px' }}>
                  <Search size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                  <p style={{ margin: 0 }}>Start typing to search for collections</p>
                </div>
              ) : (
                <>
                  {/* Filter and show collections */}
                  {(() => {
                    const filteredCollections = collections.filter(coll => 
                      coll.title.toLowerCase().includes(collectionSearch.toLowerCase())
                    );
                    
                    if (filteredCollections.length === 0) {
                      return (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d', background: '#f9fafb', borderRadius: '8px' }}>
                          <FileText size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                          <p style={{ margin: 0 }}>No collections found matching "{collectionSearch}"</p>
                        </div>
                      );
                    }
                    
                    return (
                      <>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                          {filteredCollections.length} collection{filteredCollections.length !== 1 ? 's' : ''} found
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '300px', overflowY: 'auto' }}>
                          {filteredCollections.slice(0, 10).map((coll) => (
                            <div
                              key={coll.collection_id}
                              onClick={() => {
                                setSelectedCollectionId(coll.collection_id);
                                setSelectedCollection(coll);
                                setCollectionSearch('');
                              }}
                              className="gallery-access-card"
                              style={{ 
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.75rem 1rem',
                                border: '1px solid #e9ecef',
                                borderRadius: '8px',
                                background: '#ffffff',
                                transition: 'all 0.2s'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.background = '#f0f9ff';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.borderColor = '#e9ecef';
                                e.currentTarget.style.background = '#ffffff';
                              }}
                            >
                              <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                background: '#e0f2fe',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}>
                                <FileText size={20} color="#007bff" />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <h3 style={{ 
                                  margin: 0, 
                                  fontSize: '0.9375rem', 
                                  fontWeight: 600,
                                  color: '#111827',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {coll.title}
                                </h3>
                              </div>
                              <ArrowRight size={18} color="#6c757d" style={{ flexShrink: 0 }} />
                            </div>
                          ))}
                          {filteredCollections.length > 10 && (
                            <p style={{ fontSize: '0.875rem', color: '#6b7280', textAlign: 'center', margin: '0.5rem 0' }}>
                              ... and {filteredCollections.length - 10} more. Refine your search.
                            </p>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Show collection context when embedded */}
      {isCollectionContext && propCollectionData && (
        <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f0fdf4', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
          <span style={{ fontSize: '0.875rem', color: '#166534' }}>
            📁 Collection: <strong>{propCollectionData.title}</strong>
          </span>
        </div>
      )}

      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>SELECT EMAIL TEMPLATE</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
          {emailTemplates.map((template) => {
            const audienceLabelsMap = {
              'accepted_artists': 'accepted',
              'no_artwork_submitted': 'no artwork',
              'all_artists': 'all',
              'not_accepted': 'not accepted',
              'unpaid_artists': 'unpaid',
              'organization_contacts': 'org contacts',
              'needs_terms': 'needs terms',
              'checked_in_artists': '✅ checked in',
              'not_checked_in_artists': '📦 not checked in',
              // Platform-wide audiences
              'all_users': '🌐 all users',
              'prospects': '🎯 prospects',
              'prospects_all': '🎯 all prospects',
              'prospects_linked': '🔗 linked prospects',
              'prospects_unlinked': '📧 unlinked prospects',
              'event_186_non_buyers': '🎟️ event 186 non-buyers'
            };
            const audienceLabel = audienceLabelsMap[template.audience_type] || template.audience_type || 'all';
            return (
              <div 
                key={template.template_key} 
                style={{ 
                  cursor: 'pointer', 
                  border: selectedCommunication === template.template_key ? '2px solid #7c3aed' : '2px solid #e5e7eb', 
                  borderRadius: '0.5rem', 
                  padding: '0.75rem 1rem', 
                  backgroundColor: selectedCommunication === template.template_key ? '#faf5ff' : 'white', 
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }} 
                onClick={() => setSelectedCommunication(template.template_key)}
              >
                <h4 style={{ fontWeight: '500', color: '#111827', margin: 0, fontSize: '0.9rem' }}>
                  {template.template_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h4>
                <span style={{ 
                  fontSize: '0.65rem', 
                  backgroundColor: '#f3f4f6', 
                  color: '#4b5563', 
                  padding: '0.2rem 0.4rem', 
                  borderRadius: '0.25rem',
                  alignSelf: 'flex-start'
                }}>
                  {audienceLabel}
                </span>
              </div>
            );
          })}
        </div>
        {emailTemplates.length === 0 && (
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.75rem' }}>
            No email templates found. Go to the Email Templates tab to create some.
          </p>
        )}
      </div>

      {/* Platform-Wide Communication Options - VibrantCast only */}
      {isVibrantCastOrg && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
            🌐 PLATFORM-WIDE OPTIONS <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '400' }}>(no collection required)</span>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
            {platformCommunicationOptions.map((option) => (
              <div 
                key={option.value} 
                style={{ 
                  cursor: 'pointer', 
                  border: selectedCommunication === option.value ? '2px solid #0ea5e9' : '2px solid #e5e7eb', 
                  borderRadius: '0.5rem', 
                  padding: '0.75rem 1rem', 
                  backgroundColor: selectedCommunication === option.value ? '#f0f9ff' : 'white', 
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }} 
                onClick={() => {
                  setSelectedCommunication(option.value);
                  setTargetAudience(option.audience);
                }}
              >
                <h4 style={{ fontWeight: '500', color: '#111827', margin: 0, fontSize: '0.9rem' }}>
                  {option.label}
                </h4>
                <span style={{ 
                  fontSize: '0.65rem', 
                  backgroundColor: '#dbeafe', 
                  color: '#1e40af', 
                  padding: '0.2rem 0.4rem', 
                  borderRadius: '0.25rem',
                  alignSelf: 'flex-start'
                }}>
                  {audienceLabels[option.audience] || option.audience}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                  {option.description}
                </span>
              </div>
            ))}
            
            {/* VibrantCast-Specific Options */}
            {vibrantcastCommunicationOptions.map((option) => (
              <div 
                key={option.value} 
                style={{ 
                  cursor: 'pointer', 
                  border: selectedCommunication === option.value ? '2px solid #10b981' : '2px solid #e5e7eb', 
                  borderRadius: '0.5rem', 
                  padding: '0.75rem 1rem', 
                  backgroundColor: selectedCommunication === option.value ? '#ecfdf5' : 'white', 
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }} 
                onClick={() => {
                  setSelectedCommunication(option.value);
                  setTargetAudience(option.audience);
                }}
              >
                <h4 style={{ fontWeight: '500', color: '#111827', margin: 0, fontSize: '0.9rem' }}>
                  {option.label}
                </h4>
                <span style={{ 
                  fontSize: '0.65rem', 
                  backgroundColor: '#d1fae5', 
                  color: '#047857', 
                  padding: '0.2rem 0.4rem', 
                  borderRadius: '0.25rem',
                  alignSelf: 'flex-start'
                }}>
                  {audienceLabels[option.audience] || option.audience}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                  {option.description}
                </span>
              </div>
            ))}
          </div>
          
          {/* Exclude collection artists toggle - only show for platform audiences that can be filtered */}
          {isPlatformAudience && ['all_users', 'all_artists_ever'].includes(targetAudience) && (
            <div style={{ 
              marginTop: '1rem', 
              padding: '0.75rem 1rem', 
              backgroundColor: excludeCollectionArtists ? '#fef3c7' : '#f9fafb', 
              border: excludeCollectionArtists ? '1px solid #f59e0b' : '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
          }}>
            <input 
              type="checkbox" 
              id="excludeCollectionArtists"
              checked={excludeCollectionArtists}
              onChange={(e) => setExcludeCollectionArtists(e.target.checked)}
              disabled={!selectedCollectionId}
              style={{ width: '1rem', height: '1rem', cursor: selectedCollectionId ? 'pointer' : 'not-allowed' }}
            />
            <label 
              htmlFor="excludeCollectionArtists" 
              style={{ 
                fontSize: '0.875rem', 
                color: selectedCollectionId ? '#374151' : '#9ca3af',
                cursor: selectedCollectionId ? 'pointer' : 'not-allowed'
              }}
            >
              Exclude artists in selected collection
              {selectedCollectionId && collectionArtistUuids.size > 0 && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                  ({collectionArtistUuids.size} artists will be excluded)
                </span>
              )}
              {!selectedCollectionId && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                  (select a collection first)
                </span>
              )}
            </label>
          </div>
        )}
        </div>
      )}

      {/* Organization Contacts Option - For non-VibrantCast organizations (e.g., CAVA) */}
      {!isVibrantCastOrg && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
            📇 ORGANIZATION-WIDE OPTIONS
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
            <div 
              style={{ 
                cursor: 'pointer', 
                border: targetAudience === 'organization_contacts' ? '2px solid #10b981' : '2px solid #e5e7eb', 
                borderRadius: '0.5rem', 
                padding: '0.75rem 1rem', 
                backgroundColor: targetAudience === 'organization_contacts' ? '#ecfdf5' : 'white', 
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }} 
              onClick={() => {
                setSelectedCommunication('org_contacts_outreach');
                setTargetAudience('organization_contacts');
              }}
            >
              <h4 style={{ fontWeight: '500', color: '#111827', margin: 0, fontSize: '0.9rem' }}>
                📇 All Contacts
              </h4>
              <span style={{ 
                fontSize: '0.65rem', 
                backgroundColor: '#d1fae5', 
                color: '#047857', 
                padding: '0.2rem 0.4rem', 
                borderRadius: '0.25rem',
                alignSelf: 'flex-start'
              }}>
                {submissionAudienceData.organization_contacts?.length || 0} contacts
              </span>
              <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                Send to all {organization?.name || 'organization'} contacts
              </span>
            </div>
          </div>
        </div>
      )}

      {selectedCommunication && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>TARGET AUDIENCE</label>
            {!audienceLoading && currentAudience.length > 0 && (
              <button 
                onClick={() => { setRecipientsPage(1); setShowRecipientsModal(true); }} 
                style={{ fontSize: '0.875rem', color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                <Eye style={{ height: '1rem', width: '1rem' }} />
                View all {skipAlreadySent && alreadySentCount > 0 ? `~${currentAudience.length - alreadySentCount} new` : currentAudience.length} recipients
              </button>
            )}
          </div>
          {audienceLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem', color: '#6b7280' }}>
              <Loader className="animate-spin" size={16} />
              Loading audience data...
            </div>
          ) : (
            <div style={{ marginTop: '0.5rem' }}>
              {targetAudience === 'single_artist' && singleArtist ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', backgroundColor: '#f0fdf4', border: '2px solid #22c55e', borderRadius: '0.5rem' }}>
                  <div style={{ 
                    width: '36px', 
                    height: '36px', 
                    borderRadius: '50%', 
                    backgroundColor: '#dcfce7', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#16a34a'
                  }}>
                    <User size={18} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#166534' }}>
                      {singleArtist.nickname || singleArtist.first_name || 'Artist'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#4ade80' }}>{singleArtist.email}</div>
                  </div>
                  {singleArtistMode && (
                    <button
                      onClick={() => {
                        setSingleArtistMode(false);
                        setSingleArtist(null);
                        setTargetAudience('all_artists');
                        navigate('/curator/communications', { replace: true });
                      }}
                      style={{ 
                        marginLeft: '0.5rem', 
                        padding: '0.25rem 0.5rem', 
                        fontSize: '0.6875rem', 
                        background: '#fef2f2', 
                        color: '#dc2626', 
                        border: '1px solid #fecaca', 
                        borderRadius: '4px', 
                        cursor: 'pointer' 
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              ) : targetAudience === 'accepted_artists' ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', backgroundColor: '#faf5ff', border: '2px solid #7c3aed', borderRadius: '0.5rem' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#7c3aed' }}>{audienceStats.accepted_artists || 0}</div>
                    <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>Artists</div>
                  </div>
                  <div style={{ color: '#d1d5db', fontSize: '1rem' }}>|</div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#10b981' }}>{acceptedArtworksCount}</div>
                    <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>Artworks</div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>Accepted</div>
                </div>
              ) : isPlatformAudience ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', backgroundColor: skipAlreadySent && alreadySentCount > 0 ? '#f0fdf4' : '#f0f9ff', border: `2px solid ${skipAlreadySent && alreadySentCount > 0 ? '#10b981' : '#0ea5e9'}`, borderRadius: '0.5rem' }}>
                  {platformAudienceLoading ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Loading...</span>
                    </>
                  ) : skipAlreadySent && alreadySentCount > 0 ? (
                    <>
                      <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#059669' }}>~{(audienceStats[targetAudience] || 0) - alreadySentCount}</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{audienceLabels[targetAudience] || targetAudience.replace(/_/g, ' ')}</div>
                      <div style={{ fontSize: '0.7rem', color: '#059669', marginLeft: '0.25rem' }}>({alreadySentCount} skipped)</div>
                    </>
                  ) : skipAlreadySent && checkingSentCount ? (
                    <>
                      <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0ea5e9' }}>{audienceStats[targetAudience] || 0}</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{audienceLabels[targetAudience] || targetAudience.replace(/_/g, ' ')}</div>
                      <Loader className="animate-spin" size={12} style={{ color: '#059669', marginLeft: '0.25rem' }} />
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0ea5e9' }}>{audienceStats[targetAudience] || 0}</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{audienceLabels[targetAudience] || targetAudience.replace(/_/g, ' ')}</div>
                    </>
                  )}
                </div>
              ) : targetAudience === 'organization_contacts' ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', backgroundColor: '#ecfdf5', border: '2px solid #10b981', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#10b981' }}>{audienceStats.organization_contacts || 0}</div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>All Contacts</div>
                </div>
              ) : (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', backgroundColor: '#faf5ff', border: '2px solid #7c3aed', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#7c3aed' }}>{audienceStats[targetAudience] || 0}</div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize' }}>{audienceLabels[targetAudience] || targetAudience.replace(/_/g, ' ')}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Inline Email Composer */}
      {selectedCommunication && (
        <div style={{ marginBottom: '1.5rem' }}>
          <button 
            onClick={() => setShowComposeEditor(!showComposeEditor)} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              marginBottom: showComposeEditor ? '1rem' : 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151'
            }}
          >
            {showComposeEditor ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            EMAIL CONTENT
            {composeSubject && (
              <span style={{ fontWeight: 'normal', color: '#6b7280', marginLeft: '0.5rem' }}>
                — {composeSubject.substring(0, 40)}{composeSubject.length > 40 ? '...' : ''}
              </span>
            )}
          </button>
          
          {showComposeEditor && (
            <div style={{ 
              padding: '1.25rem', 
              backgroundColor: '#f9fafb', 
              borderRadius: '0.5rem', 
              border: '1px solid #e5e7eb' 
            }}>
              {/* Subject Line */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block',
                  fontSize: '0.75rem', 
                  fontWeight: '600', 
                  color: '#6b7280', 
                  marginBottom: '0.375rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Subject Line
                </label>
                <input
                  type="text"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    backgroundColor: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              {/* Email Body */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                  <label style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: '600', 
                    color: '#6b7280', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Email Body
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          navigator.clipboard.writeText(e.target.value);
                          // Show feedback by briefly changing border color
                          const select = e.target;
                          const originalBorder = select.style.borderColor;
                          select.style.borderColor = '#16a34a';
                          setTimeout(() => { select.style.borderColor = originalBorder; }, 500);
                          e.target.value = '';
                        }
                      }}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.25rem',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        color: '#6b7280',
                        minWidth: '140px'
                      }}
                    >
                      <option value="">+ Insert Field</option>
                      {mergeVariables.map((mv) => (
                        <option key={mv.key} value={mv.key}>
                          {mv.description}
                        </option>
                      ))}
                    </select>
                    <span style={{ fontSize: '0.625rem', color: '#9ca3af' }}>paste where needed</span>
                  </div>
                </div>
                <div style={{ backgroundColor: 'white', borderRadius: '0.375rem', border: '1px solid #d1d5db' }}>
                  <RichTextEditor
                    value={composeBody}
                    onChange={setComposeBody}
                    placeholder="Compose your email..."
                    minHeight="200px"
                    enableImageUpload={true}
                    imageFolder="email-images"
                  />
                </div>
              </div>

              {/* Template info if using one */}
              {emailTemplates.find(t => t.template_key === selectedCommunication) && (
                <div style={{ 
                  marginTop: '0.75rem', 
                  fontSize: '0.75rem', 
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <Info size={12} />
                  Using template: {emailTemplates.find(t => t.template_key === selectedCommunication)?.template_name}
                  {' '}— you can edit the content above before sending
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Email Preview Button */}
      {selectedCommunication && (
        <div style={{ marginBottom: '1.5rem' }}>
          <button 
            onClick={() => setShowSendEmailPreview(!showSendEmailPreview)} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              padding: '0.75rem 1rem',
              backgroundColor: showSendEmailPreview ? '#7c3aed' : '#f3f4f6',
              color: showSendEmailPreview ? 'white' : '#374151',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            <Mail size={18} />
            {showSendEmailPreview ? 'Hide Email Preview' : 'Preview Email'}
          </button>
        </div>
      )}

      {/* Email Preview Card */}
      {showSendEmailPreview && selectedCommunication && (() => {
        const emailContent = getSelectedEmailContent();
        const recipients = getRecipientsDisplay();
        // Use collection's organization info for accurate preview
        const collectionOrg = selectedCollection?.organizations || propCollectionData?.organizations || organization;
        const fromName = collectionOrg?.name || organization?.name || 'VibrantCast';
        const fromEmail = collectionOrg?.contact_email || organization?.contact_email || 'noreply@vibrantcast.com';
        
        if (!emailContent) return null;
        
        return (
          <div style={{ 
            marginBottom: '1.5rem', 
            backgroundColor: 'white', 
            borderRadius: '0.75rem', 
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            {/* Email Header */}
            <div style={{ 
              backgroundColor: testMode ? '#fef3c7' : '#f0fdf4', 
              padding: '1rem 1.25rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                marginBottom: '0.75rem',
                color: testMode ? '#92400e' : '#166534',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                {testMode ? (
                  <>
                    <AlertTriangle size={16} />
                    TEST MODE - Email will be sent to selected admins only
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    PRODUCTION - Email will be sent to {currentAudience.length} recipients
                  </>
                )}
              </div>

              {/* Artist Preview Selector - for portfolio emails */}
              {targetAudience === 'vc_artists' && currentAudience.length > 0 && (
                <div style={{ marginBottom: '0.75rem', padding: '0.75rem', backgroundColor: '#eff6ff', borderRadius: '0.5rem', border: '1px solid #bfdbfe' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1e40af', display: 'block', marginBottom: '0.375rem' }}>
                    👁️ PREVIEW AS ARTIST (see their real portfolio data)
                  </label>
                  
                  {/* Selected artist display */}
                  {previewArtistUuid && (() => {
                    const selectedArtist = currentAudience.find(a => a.uuid === previewArtistUuid);
                    return selectedArtist ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#dbeafe',
                        borderRadius: '0.375rem',
                        marginBottom: '0.5rem'
                      }}>
                        <span style={{ fontSize: '0.875rem', color: '#1e40af', fontWeight: '500' }}>
                          {selectedArtist.nickname || selectedArtist.first_name || 'Artist'} ({selectedArtist.email})
                        </span>
                        <button
                          onClick={() => {
                            setPreviewArtistUuid(null);
                            setPreviewPortfolioData(null);
                            setPreviewArtistSearch('');
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer'
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    ) : null;
                  })()}
                  
                  {/* Search input */}
                  {!previewArtistUuid && (
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={previewArtistSearch}
                        onChange={(e) => {
                          setPreviewArtistSearch(e.target.value);
                          setShowPreviewArtistResults(e.target.value.length > 0);
                        }}
                        onFocus={() => setShowPreviewArtistResults(previewArtistSearch.length > 0)}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #93c5fd',
                          borderRadius: '0.375rem',
                          backgroundColor: 'white'
                        }}
                      />
                      
                      {/* Search results dropdown */}
                      {showPreviewArtistResults && previewArtistSearch.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          backgroundColor: 'white',
                          border: '1px solid #93c5fd',
                          borderRadius: '0.375rem',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          zIndex: 100
                        }}>
                          {(() => {
                            const searchLower = previewArtistSearch.toLowerCase();
                            const filtered = currentAudience.filter(artist => 
                              (artist.nickname || '').toLowerCase().includes(searchLower) ||
                              (artist.first_name || '').toLowerCase().includes(searchLower) ||
                              (artist.last_name || '').toLowerCase().includes(searchLower) ||
                              (artist.email || '').toLowerCase().includes(searchLower)
                            ).slice(0, 20);
                            
                            if (filtered.length === 0) {
                              return (
                                <div style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.875rem', textAlign: 'center' }}>
                                  No artists found matching "{previewArtistSearch}"
                                </div>
                              );
                            }
                            
                            return filtered.map((artist) => (
                              <div
                                key={artist.uuid}
                                onClick={() => {
                                  setPreviewArtistUuid(artist.uuid);
                                  setPreviewArtistSearch('');
                                  setShowPreviewArtistResults(false);
                                }}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #e5e7eb',
                                  fontSize: '0.875rem'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                              >
                                <div style={{ fontWeight: '500', color: '#111827' }}>
                                  {artist.nickname || `${artist.first_name || ''} ${artist.last_name || ''}`.trim() || 'Unknown'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{artist.email}</div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {loadingPreviewData && (
                    <p style={{ fontSize: '0.75rem', color: '#3b82f6', marginTop: '0.375rem' }}>Loading portfolio data...</p>
                  )}
                </div>
              )}

              {/* To: field */}
              <div style={{ marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginRight: '0.5rem' }}>To:</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.25rem' }}>
                  {recipients.map((r, i) => (
                    <span 
                      key={i} 
                      style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem', 
                        backgroundColor: testMode ? '#fef3c7' : '#dbeafe', 
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        color: testMode ? '#92400e' : '#1e40af'
                      }}
                    >
                      {r.name} 
                      <span style={{ color: testMode ? '#b45309' : '#3b82f6', opacity: 0.8 }}>
                        &lt;{r.email}&gt;
                      </span>
                      {r.role !== 'artist' && (
                        <span style={{ 
                          padding: '0.125rem 0.375rem', 
                          backgroundColor: 'rgba(0,0,0,0.1)', 
                          borderRadius: '4px',
                          fontSize: '0.625rem',
                          textTransform: 'uppercase'
                        }}>
                          {r.role}
                        </span>
                      )}
                    </span>
                  ))}
                  {!testMode && currentAudience.length > 5 && (
                    <span style={{ 
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem',
                      color: '#6b7280'
                    }}>
                      +{currentAudience.length - 5} more
                    </span>
                  )}
                </div>
              </div>

              {/* Subject */}
              <div>
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginRight: '0.5rem' }}>Subject:</span>
                <span style={{ fontSize: '0.9375rem', fontWeight: '500', color: '#111827' }}>
                  {composeSubject || emailContent.subject}
                </span>
              </div>
            </div>

            {/* Email Body */}
            <div style={{ padding: '1.25rem' }}>
              {composeBody ? (
                <div 
                  style={{ 
                    fontSize: '0.9375rem', 
                    lineHeight: '1.6', 
                    color: '#374151',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ __html: getPreviewHtml(composeBody) }}
                />
              ) : emailContent.html_body ? (
                <div 
                  style={{ 
                    fontSize: '0.9375rem', 
                    lineHeight: '1.6', 
                    color: '#374151',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ __html: getPreviewHtml(emailContent.html_body) }}
                />
              ) : emailContent.template_name ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  backgroundColor: '#f9fafb', 
                  borderRadius: '0.5rem',
                  border: '2px dashed #d1d5db'
                }}>
                  <FileText size={32} style={{ color: '#9ca3af', marginBottom: '0.5rem' }} />
                  <p style={{ margin: '0 0 0.25rem 0', fontWeight: '500', color: '#374151' }}>
                    Using Mandrill Template
                  </p>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                    {emailContent.template_name}
                  </p>
                </div>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  <p>No email content configured for this communication type.</p>
                </div>
              )}
            </div>

            {/* Email Footer */}
            <div style={{ 
              backgroundColor: '#f9fafb', 
              padding: '0.75rem 1.25rem', 
              borderTop: '1px solid #e5e7eb',
              fontSize: '0.75rem',
              color: '#6b7280',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>
                From: {fromName} &lt;{fromEmail}&gt;
              </span>
              <span>
                {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
        );
      })()}

      {selectedCommunication === 'event_announcement' && (
        <div style={{ marginBottom: '1.5rem', backgroundColor: '#eff6ff', borderRadius: '0.5rem', padding: '1rem' }}>
          <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '0.75rem' }}>Event Details</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Event Date</label>
              <input type="datetime-local" value={eventDetails.event_date} onChange={(e) => setEventDetails({ ...eventDetails, event_date: e.target.value })} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Location</label>
              <input type="text" value={eventDetails.event_location} onChange={(e) => setEventDetails({ ...eventDetails, event_location: e.target.value })} placeholder="Gallery address" style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Setup Time</label>
              <input type="text" value={eventDetails.setup_time} onChange={(e) => setEventDetails({ ...eventDetails, setup_time: e.target.value })} placeholder="2 hours before opening" style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
            </div>
          </div>
        </div>
      )}

      {selectedCommunication === 'general_update' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Custom Message</label>
          <textarea value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} placeholder="Enter your message here..." rows={4} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontFamily: 'inherit' }} />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          {selectedCommunication && (
            testMode ? (
              <>
                <span style={{ color: '#ea580c' }}>Test mode:</span> Will send to{' '}
                <button
                  onClick={() => setShowTestAdminsModal(true)}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    padding: 0, 
                    cursor: 'pointer', 
                    color: '#7c3aed', 
                    fontWeight: '600',
                    textDecoration: 'underline'
                  }}
                >
                  {selectedTestAdminIds.length > 0 ? selectedTestAdminIds.length : orgMembers.length} admin{(selectedTestAdminIds.length > 0 ? selectedTestAdminIds.length : orgMembers.length) !== 1 ? 's' : ''}
                </button>
                <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>(would reach {currentAudience.length} recipients in live mode)</span>
              </>
            ) : (
              <>
                {skipAlreadySent && checkingSentCount ? (
                  <>
                    Will send to <strong>{currentAudience.length}</strong> recipients
                    <span style={{ color: '#0284c7', marginLeft: '0.5rem' }}>(checking already sent...)</span>
                  </>
                ) : skipAlreadySent && alreadySentCount != null && alreadySentCount > 0 ? (
                  <>
                    Will send to <strong style={{ color: '#059669' }}>~{currentAudience.length - alreadySentCount}</strong> new recipients
                    <span style={{ color: '#059669', marginLeft: '0.5rem' }}>({alreadySentCount} already sent will be skipped)</span>
                  </>
                ) : skipAlreadySent && alreadySentCount != null && alreadySentCount === 0 ? (
                  <>
                    Will send to <strong>{currentAudience.length}</strong> recipients
                    <span style={{ color: '#059669', marginLeft: '0.5rem' }}>(none previously sent)</span>
                  </>
                ) : (
                  <>
                    Will send to <strong>{currentAudience.length}</strong> recipients
                  </>
                )}
                {excludedUserIds.size > 0 && (
                  <span style={{ color: '#dc2626', marginLeft: '0.5rem' }}>({excludedUserIds.size} excluded)</span>
                )}
                {fullAudience.length > 0 && (
                  <button
                    onClick={() => { setRecipientsPage(1); setShowRecipientsModal(true); }}
                    style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.75rem', color: '#7c3aed', background: 'none', border: '1px solid #7c3aed', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    View All
                  </button>
                )}
              </>
            )
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} style={{ height: '1rem', width: '1rem' }} />
            <span style={{ fontSize: '0.875rem', fontWeight: '500', color: testMode ? '#ea580c' : '#6b7280' }}>Test Mode</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} title="Skip recipients who already received an email with the same subject (useful for resending after a partial failure)">
            <input type="checkbox" checked={skipAlreadySent} onChange={(e) => setSkipAlreadySent(e.target.checked)} style={{ height: '1rem', width: '1rem' }} />
            <span style={{ fontSize: '0.875rem', fontWeight: '500', color: skipAlreadySent ? '#059669' : '#6b7280' }}>Skip Already Sent</span>
          </label>
          <button onClick={sendCommunication} disabled={!selectedCommunication || isLoading} style={{ padding: '0.75rem 1.5rem', borderRadius: '0.375rem', fontWeight: '500', border: 'none', cursor: !selectedCommunication || isLoading ? 'not-allowed' : 'pointer', backgroundColor: !selectedCommunication || isLoading ? '#d1d5db' : testMode ? '#ea580c' : '#7c3aed', color: !selectedCommunication || isLoading ? '#6b7280' : 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {isLoading ? (<><Loader style={{ height: '1rem', width: '1rem', animation: 'spin 1s linear infinite' }} />Sending...</>) : (<><Send style={{ height: '1rem', width: '1rem' }} />{testMode ? 'Send Test' : 'Send Communication'}</>)}
          </button>
        </div>
      </div>

      {/* Sending Progress Panel */}
      {isLoading && sendingStatus && (
        <div style={{ marginTop: '1rem', padding: '1rem', borderRadius: '0.5rem', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '1.5rem',
              height: '1.5rem',
              border: '2px solid #0284c7',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <div>
              <p style={{ margin: 0, fontWeight: '500', color: '#0369a1' }}>{sendingStatus}</p>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#0284c7' }}>Please wait while emails are being sent. Do not close this page.</p>
            </div>
          </div>
          {/* Progress bar animation */}
          <div style={{ marginTop: '0.75rem', height: '4px', backgroundColor: '#e0f2fe', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              backgroundColor: '#0284c7',
              borderRadius: '2px',
              animation: 'progress-indeterminate 1.5s ease-in-out infinite',
              width: '30%'
            }} />
          </div>
        </div>
      )}

      {results && (
        <div style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: '0.5rem', backgroundColor: results.success ? '#f0fdf4' : '#fef2f2', border: results.success ? '1px solid #bbf7d0' : '1px solid #fecaca' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            {results.success ? <CheckCircle style={{ height: '1.25rem', width: '1.25rem', color: '#16a34a', marginRight: '0.5rem' }} /> : <AlertTriangle style={{ height: '1.25rem', width: '1.25rem', color: '#dc2626', marginRight: '0.5rem' }} />}
            <h4 style={{ fontWeight: '500', color: results.success ? '#166534' : '#dc2626', margin: 0 }}>{results.success ? (results.test_mode ? 'Test Communication Sent!' : 'Communication Sent Successfully!') : 'Communication Failed'}</h4>
          </div>
          {results.success ? (<div style={{ fontSize: '0.875rem', color: '#166534' }}>{results.test_mode ? <div><p>Preview sent to admin emails. Would reach {results.preview_data?.target_users} users.</p></div> : <div><p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Mail style={{ height: '1rem', width: '1rem' }} />Emails sent: {results.results?.email_sent || 0} of {results.results?.total_users || '?'} recipients</p>{results.results?.skipped > 0 && <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280' }}>Skipped (user preferences): {results.results.skipped}</p>}{results.results?.errors?.length > 0 && <p style={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><AlertTriangle style={{ height: '1rem', width: '1rem' }} />{results.results.errors.length} error{results.results.errors.length !== 1 ? 's' : ''} occurred</p>}</div>}</div>) : (<p style={{ fontSize: '0.875rem', color: '#dc2626' }}>{results.error}</p>)}
        </div>
      )}

      {/* Test Admins Selection Modal */}
      {showTestAdminsModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', maxWidth: '600px', width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
            {/* Header */}
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>
                  Select Test Admins ({selectedTestAdminIds.length > 0 ? selectedTestAdminIds.length : orgMembers.length} of {orgMembers.length})
                </h3>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>
                  Choose which admins should receive the test email
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setSelectedTestAdminIds([])}
                  style={{ padding: '0.375rem 0.75rem', fontSize: '0.8125rem', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
                >
                  Select All
                </button>
                <button
                  onClick={() => setShowTestAdminsModal(false)}
                  style={{ padding: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Admin List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
              {orgMembers.map((member) => {
                const isSelected = selectedTestAdminIds.length === 0 || selectedTestAdminIds.includes(member.id);
                const user = member.users;
                const displayName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : 'Unknown';
                
                return (
                  <div
                    key={member.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem 1rem',
                      margin: '0.25rem 0',
                      backgroundColor: isSelected ? '#faf5ff' : '#f9fafb',
                      borderRadius: '0.5rem',
                      border: isSelected ? '1px solid #c4b5fd' : '1px solid transparent'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: isSelected ? '#7c3aed' : '#d1d5db',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: '600'
                      }}>
                        {(user?.first_name?.[0] || user?.email?.[0] || '?').toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', color: '#111827', fontSize: '0.9375rem' }}>{displayName}</div>
                        <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{user?.email}</div>
                      </div>
                      <span style={{ 
                        fontSize: '0.6875rem', 
                        padding: '0.125rem 0.375rem', 
                        backgroundColor: member.role === 'owner' ? '#fef3c7' : '#e0e7ff',
                        color: member.role === 'owner' ? '#92400e' : '#3730a3',
                        borderRadius: '0.25rem',
                        textTransform: 'capitalize'
                      }}>
                        {member.role}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {isSelected ? (
                        <button
                          onClick={() => {
                            // If all are selected (empty array), switch to explicitly selected list minus this one
                            if (selectedTestAdminIds.length === 0) {
                              setSelectedTestAdminIds(orgMembers.filter(m => m.id !== member.id).map(m => m.id));
                            } else {
                              setSelectedTestAdminIds(prev => prev.filter(id => id !== member.id));
                            }
                          }}
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                        >
                          Exclude
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedTestAdminIds(prev => [...prev, member.id]);
                          }}
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem', backgroundColor: '#dcfce7', color: '#16a34a', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                        >
                          Include
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Footer */}
            <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowTestAdminsModal(false)}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '500' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recipients List Modal */}
      {showRecipientsModal && (() => {
        // Helper to get display name
        const getDisplayName = (artist) => artist.nickname || 
          (artist.first_name ? `${artist.first_name} ${artist.last_name || ''}`.trim() : null) ||
          artist.email?.split('@')[0] || 'Unknown';
        
        // Sort the full audience (including excluded for display)
        const sortedAudience = [...fullAudience].sort((a, b) => {
          // Put excluded at the bottom
          const aExcluded = excludedUserIds.has(a.uuid);
          const bExcluded = excludedUserIds.has(b.uuid);
          if (aExcluded !== bExcluded) return aExcluded ? 1 : -1;
          
          let aVal, bVal;
          switch (recipientsSortField) {
            case 'name':
              aVal = getDisplayName(a).toLowerCase();
              bVal = getDisplayName(b).toLowerCase();
              break;
            case 'email':
              aVal = (a.email || '').toLowerCase();
              bVal = (b.email || '').toLowerCase();
              break;
            case 'status':
              // Sort by: Accepted > Artwork > Paid > Unpaid
              aVal = (a.has_accepted_artwork ? 3 : 0) + (a.has_submitted_artwork ? 2 : 0) + (a.has_paid ? 1 : 0);
              bVal = (b.has_accepted_artwork ? 3 : 0) + (b.has_submitted_artwork ? 2 : 0) + (b.has_paid ? 1 : 0);
              break;
            default:
              aVal = getDisplayName(a).toLowerCase();
              bVal = getDisplayName(b).toLowerCase();
          }
          if (recipientsSortDir === 'asc') {
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          } else {
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
          }
        });
        
        const totalPages = Math.ceil(sortedAudience.length / recipientsPerPage);
        const excludedCount = excludedUserIds.size;
        
        // Sort header component
        const SortHeader = ({ field, label }) => (
          <th 
            onClick={() => {
              if (recipientsSortField === field) {
                setRecipientsSortDir(d => d === 'asc' ? 'desc' : 'asc');
              } else {
                setRecipientsSortField(field);
                setRecipientsSortDir('asc');
              }
              setRecipientsPage(1);
            }}
            style={{ 
              padding: '0.75rem 1rem', 
              textAlign: 'left', 
              fontSize: '0.75rem', 
              fontWeight: '600', 
              color: recipientsSortField === field ? '#7c3aed' : '#6b7280', 
              textTransform: 'uppercase', 
              borderBottom: '1px solid #e5e7eb',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            {label} {recipientsSortField === field && (recipientsSortDir === 'asc' ? '↑' : '↓')}
          </th>
        );
        
        return (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 9999 
          }}>
            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '12px', 
              width: '90%', 
              maxWidth: '700px', 
              maxHeight: '80vh', 
              display: 'flex', 
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              {/* Modal Header */}
              <div style={{ 
                padding: '1.25rem 1.5rem', 
                borderBottom: '1px solid #e5e7eb', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>
                    Recipients ({currentAudience.length} of {fullAudience.length})
                  </h3>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                    {audienceLabels[targetAudience] || 'Selected audience'} for {selectedCollection?.title || 'this collection'}
                    {excludedCount > 0 && (
                      <span style={{ marginLeft: '0.5rem', color: '#dc2626' }}>
                        • {excludedCount} excluded
                      </span>
                    )}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {/* Exclude All button - makes it easy to then include just one */}
                  {excludedCount < fullAudience.length && (
                    <button 
                      onClick={() => {
                        const allUuids = new Set(fullAudience.map(a => a.uuid));
                        setExcludedUserIds(allUuids);
                      }}
                      style={{ 
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.75rem',
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        border: '1px solid #fecaca',
                        borderRadius: '0.375rem',
                        cursor: 'pointer'
                      }}
                    >
                      Exclude All
                    </button>
                  )}
                  {excludedCount > 0 && (
                    <button 
                      onClick={() => setExcludedUserIds(new Set())}
                      style={{ 
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.75rem',
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        border: '1px solid #bbf7d0',
                        borderRadius: '0.375rem',
                        cursor: 'pointer'
                      }}
                    >
                      Include All
                    </button>
                  )}
                  <button 
                    onClick={() => setShowRecipientsModal(false)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer', 
                      padding: '0.5rem',
                      color: '#6b7280'
                    }}
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Recipients Table */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', width: '50px' }}>#</th>
                      <SortHeader field="name" label="Name" />
                      <SortHeader field="email" label="Email" />
                      <SortHeader field="status" label="Status" />
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', width: '80px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAudience
                      .slice((recipientsPage - 1) * recipientsPerPage, recipientsPage * recipientsPerPage)
                      .map((artist, index) => {
                        const displayName = getDisplayName(artist);
                        const globalIndex = (recipientsPage - 1) * recipientsPerPage + index + 1;
                        const isExcluded = excludedUserIds.has(artist.uuid);
                        return (
                          <tr key={artist.uuid || index} style={{ 
                            borderBottom: '1px solid #f3f4f6',
                            backgroundColor: isExcluded ? '#fef2f2' : 'transparent',
                            opacity: isExcluded ? 0.6 : 1
                          }}>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#9ca3af' }}>{globalIndex}</td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: isExcluded ? '#9ca3af' : '#111827', fontWeight: '500', textDecoration: isExcluded ? 'line-through' : 'none' }}>{displayName}</td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#6b7280', textDecoration: isExcluded ? 'line-through' : 'none' }}>{artist.email || '—'}</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                {isExcluded && (
                                  <span style={{ padding: '0.125rem 0.5rem', backgroundColor: '#fecaca', color: '#dc2626', fontSize: '0.6875rem', borderRadius: '9999px' }}>Excluded</span>
                                )}
                                {!isExcluded && artist.has_paid && (
                                  <span style={{ padding: '0.125rem 0.5rem', backgroundColor: '#dcfce7', color: '#166534', fontSize: '0.6875rem', borderRadius: '9999px' }}>Paid</span>
                                )}
                                {!isExcluded && !artist.has_paid && (
                                  <span style={{ padding: '0.125rem 0.5rem', backgroundColor: '#fef3c7', color: '#92400e', fontSize: '0.6875rem', borderRadius: '9999px' }}>Unpaid</span>
                                )}
                                {!isExcluded && artist.has_submitted_artwork && (
                                  <span style={{ padding: '0.125rem 0.5rem', backgroundColor: '#dbeafe', color: '#1e40af', fontSize: '0.6875rem', borderRadius: '9999px' }}>Artwork</span>
                                )}
                                {!isExcluded && artist.has_accepted_artwork && (
                                  <span style={{ padding: '0.125rem 0.5rem', backgroundColor: '#f3e8ff', color: '#7c3aed', fontSize: '0.6875rem', borderRadius: '9999px' }}>Accepted</span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                                <button
                                  onClick={() => {
                                    setExcludedUserIds(prev => {
                                      const newSet = new Set(prev);
                                      if (newSet.has(artist.uuid)) {
                                        newSet.delete(artist.uuid);
                                      } else {
                                        newSet.add(artist.uuid);
                                      }
                                      return newSet;
                                    });
                                  }}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.6875rem',
                                    fontWeight: '500',
                                    backgroundColor: isExcluded ? '#dcfce7' : '#fef2f2',
                                    color: isExcluded ? '#166534' : '#dc2626',
                                    border: `1px solid ${isExcluded ? '#bbf7d0' : '#fecaca'}`,
                                    borderRadius: '0.25rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {isExcluded ? 'Include' : 'Exclude'}
                                </button>
                                {!isExcluded && fullAudience.length > 1 && (
                                  <button
                                    onClick={() => {
                                      // Exclude all except this one
                                      const allExceptThis = new Set(fullAudience.filter(a => a.uuid !== artist.uuid).map(a => a.uuid));
                                      setExcludedUserIds(allExceptThis);
                                    }}
                                    style={{
                                      padding: '0.25rem 0.5rem',
                                      fontSize: '0.6875rem',
                                      fontWeight: '500',
                                      backgroundColor: '#f0f9ff',
                                      color: '#0369a1',
                                      border: '1px solid #bae6fd',
                                      borderRadius: '0.25rem',
                                      cursor: 'pointer'
                                    }}
                                    title="Send only to this person"
                                  >
                                    Only
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Footer */}
              <div style={{ 
                padding: '1rem 1.5rem', 
                borderTop: '1px solid #e5e7eb', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                backgroundColor: '#f9fafb',
                flexWrap: 'wrap',
                gap: '0.75rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Showing {Math.min((recipientsPage - 1) * recipientsPerPage + 1, sortedAudience.length)} - {Math.min(recipientsPage * recipientsPerPage, sortedAudience.length)} of {sortedAudience.length}
                  </span>
                  <select
                    value={recipientsPerPage}
                    onChange={(e) => {
                      setRecipientsPerPage(Number(e.target.value));
                      setRecipientsPage(1);
                    }}
                    style={{
                      padding: '0.375rem 0.5rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      color: '#374151',
                      cursor: 'pointer'
                    }}
                  >
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    onClick={() => setRecipientsPage(p => Math.max(1, p - 1))}
                    disabled={recipientsPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: recipientsPage === 1 ? '#f3f4f6' : 'white',
                      color: recipientsPage === 1 ? '#9ca3af' : '#374151',
                      cursor: recipientsPage === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem', color: '#374151' }}>
                    Page {recipientsPage} of {totalPages || 1}
                  </span>
                  <button
                    onClick={() => setRecipientsPage(p => Math.min(totalPages, p + 1))}
                    disabled={recipientsPage >= totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: recipientsPage >= totalPages ? '#f3f4f6' : 'white',
                      color: recipientsPage >= totalPages ? '#9ca3af' : '#374151',
                      cursor: recipientsPage >= totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CuratorCommunicationsHub;
